import CreativeEngine from "@cesdk/engine";
import { useState, useEffect, useRef, useCallback} from "react";
import {
    type InvitationResponse,
    type Presentation, createSession, storeSessionVariables, type SessionVariable, markFinished, markStarted,
    slideStarted, startPostALinkSession
} from "./api/viewSessions.ts";
import {
    type BlockAction, getSlides, type SlideInfo
} from "./api/presentations.ts";
import type { DesignBlockId } from "@cesdk/cesdk-js";
import {usePlayerConfig} from './config.ts';
import PlayPresentationButton from "./components/PlayPresentationButton.tsx";
import RecipientInfoModal from "./components/RecipientInfoModal.tsx";
import {
    injectStyles,
    setOverlayPluginStyles,
    splitName
} from "./utils/utilities.ts";
import {mountSliderOverlays} from "./interactions/slider-plugin.ts";
import {findAllLogoReveals, getLogoRevealMeta, mountLogoRevealOverlays} from "./interactions/logo-reveal-plugin.ts";
import {mountMenuButtonsForPlayer} from "./interactions/menu-button-plugin.ts";
import {
    collectInputFieldSubmission,
    findAllInputFields,
    getInputFieldMeta,
    mountInputFieldOverlays,
    validateRequiredInputFields,
    type InputFieldMeta,
    type InputFieldSubmissionValue
} from "./interactions/input-field-plugin.ts";
import { MUSIC_BASE_URL } from "./api/config.ts";
import { useSongs } from "./hooks/useSongs.tsx";
import { type VirtualizedSlideInfo } from "./state/virtualizedSlide.ts";
import {
    BLOCK_ACTION_META_KEY,
    DYNAMIC_VARIABLES_UPDATED_EVENT,
    PRESENTATION_HEIGHT,
    PRESENTATION_WIDTH
} from "./constants.ts";
import {findOwningPage, getAbsoluteStartOnPage, reindexSlides} from "./utils/slideUtilities.ts";
import {
    filterActionsForSlide,
} from "./utils/blockActions.ts";
import {createClickActionOverlayElements, repositionClickActionOverlayElements} from "./utils/interactionOverlay.ts";
import * as SoundPlayer from "./playback/sound-player.ts";
import * as MusicPlayer from "./playback/music-player.ts";
import * as AudioContext from "./playback/audio-context.ts";
import { startFpsOverlay } from "./playback/fps-counter.ts";
import * as DeviceCompatibility from "./playback/device-compatibility.ts";
import * as VideoRenderer from "./playback/video-renderer.ts";
import {isMobile} from "./utils/browser.ts";
import {
    evaluateCalculatorsForPage,
    getCalculatorTemplateTextForBlock,
    hasDelimitedCalculatorBlocks,
    readDynamicVariableDisplayValue,
    replaceDelimitedCalculatorBlocks,
    syncNamedTextBlockVariablesForScene
} from "./utils/calculator.ts";
import {readWindowDynamicVariableEntry, writeChoiceVariable} from "./utils/dynamicVariables.ts";
import Watermark, { type WatermarkHandle } from "./components/Watermark.tsx";
import LoadingOverlay from "./components/LoadingOverlay.tsx";
import * as audioContext from "./playback/audio-context.ts";

const DEFAULT_SLIDE_DURATION_MS = 5000;
const SLIDES_MAX_PRECACHE = 14;
const BASE_MUSIC_VOLUME = 0.07;
const DUCKED_MUSIC_VOLUME = 0.03;
const LOGO_REVEAL_END_PAUSE_BUFFER_SEC = 0.05;
const DEBUG_SLOW_SLIDE_CHANGE_PARAM = 'slowSlideChangeMs';
const DEBUG_SLOW_SLIDE_CHANGE_DEFAULT_MS = 2000;

function getDebugSlowSlideChangeDelayMs(): number {
    if (typeof window === 'undefined') return 0;

    const params = new URLSearchParams(window.location.search);
    if (!params.has(DEBUG_SLOW_SLIDE_CHANGE_PARAM)) return 0;

    const raw = params.get(DEBUG_SLOW_SLIDE_CHANGE_PARAM)?.trim() ?? '';
    if (raw === '') {
        return DEBUG_SLOW_SLIDE_CHANGE_DEFAULT_MS;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
    }

    return Math.round(parsed);
}

interface PlayerProps{
    urlCode: string | undefined;
    previewToken?: string;
}

type RecipientInfo = {
    name: string;
    email: string;
    phone: string;
};

type FinishPayload = {
    actionResponse?: string;
    actionResponseStatus?: string;
};

export default function Player({urlCode, previewToken}:PlayerProps) {
    // const navigate= useNavigate();

    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const playbackTimeoutRef = useRef<number | null>(null);
    const pendingAutoAdvanceRef = useRef<{ slideId: number; targetSlideId: number } | null>(null);
    const pendingAutoAdvanceIntervalRef = useRef<number | null>(null);
    const pendingAutoAdvanceTimeoutRef = useRef<number | null>(null);
    const slideTransitionLockRef = useRef(false);
    const slideTransitionOverlayTimeoutRef = useRef<number | null>(null);
    const sliderControllerRef = useRef<{ updateForActivePage: (pageId: number) => void; cleanup: () => void } | null>(null);
    const logoRevealControllerRef = useRef<{ updateForActivePage: (pageId: number) => void; cleanup: () => void } | null>(null);
    const menuButtonControllerRef = useRef<{ updateForActivePage: (pageId: number) => void; cleanup: () => void } | null>(null);
    const inputFieldControllerRef = useRef<{ updateForActivePage: (pageId: number) => void; cleanup: () => void } | null>(null);

    const soundDisposerRef = useRef<(() => void) | null>(null);
    const watermarkRef = useRef<WatermarkHandle | null>(null);

    const [engine, setEngine] = useState<CreativeEngine | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [pages, setPages] = useState<DesignBlockId[]>([]);
    const [isLoadingOverlayVisible, setIsLoadingOverlayVisible] = useState(true);
    const [isSlideTransitionPending, setIsSlideTransitionPending] = useState(false);
    const [isSlideTransitionLoadingVisible, setIsSlideTransitionLoadingVisible] = useState(false);
    const [loadingOverlayAspectRatio, setLoadingOverlayAspectRatio] = useState<number | null>(null);
    const setLoadingOverlayVisible = useCallback((visible: boolean) => {
        setIsLoadingOverlayVisible(visible);
    }, []);
    const beginSlideTransition = useCallback(() => {
        if (slideTransitionOverlayTimeoutRef.current != null) {
            clearTimeout(slideTransitionOverlayTimeoutRef.current);
        }
        setIsSlideTransitionPending(true);
        setIsSlideTransitionLoadingVisible(false);
        slideTransitionOverlayTimeoutRef.current = window.setTimeout(() => {
            setIsSlideTransitionLoadingVisible(true);
        }, 500);
    }, []);
    const endSlideTransition = useCallback(() => {
        if (slideTransitionOverlayTimeoutRef.current != null) {
            clearTimeout(slideTransitionOverlayTimeoutRef.current);
            slideTransitionOverlayTimeoutRef.current = null;
        }
        setIsSlideTransitionPending(false);
        setIsSlideTransitionLoadingVisible(false);
    }, []);

    const [isPlaying, setIsPlaying] = useState(false);
    const isPlayingRef = useRef<boolean>(false);
    const [activePage, setActivePage] = useState(0);
    const activePageRef = useRef<DesignBlockId>(0);
    const [playbackNonce, setPlaybackNonce] = useState(0);
    const lastPlaybackNonceRef = useRef(0);
    const lastActivePageRef = useRef(0);
    const [activeSlideId, setActiveSlideId] = useState<number | null>(null);
    const [blockActions, setBlockActions] = useState<BlockAction[]>([]);
    const blockActionsRef = useRef<BlockAction[]>([]);
    const [virtualizedSlideInfo, setVirtualizedSlideInfo] = useState<VirtualizedSlideInfo[]>([]);
    const [startClicked, setStartClicked] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const completedLogoRevealsRef = useRef<Set<number>>(new Set());
    const [selectedSong, setSelectedSong] = useState('none');
    const [selectedMusicUrl, setSelectedMusicUrl] = useState<string | null>(null);
    const [sessionData, setSessionData] = useState<InvitationResponse | null>(null);
    const [presentationData, setPresentationData] = useState<Presentation | null>(null);
    const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
    const [isRecipientModalSubmitting, setIsRecipientModalSubmitting] = useState(false);
    const [recipientModalError, setRecipientModalError] = useState<string | null>(null);

    const isPausedForLogoRevealRef = useRef<boolean>(false);
    const remainingSlideTimeRef = useRef<number>(0);
    const delayUntilVisibleRef = useRef<number>(0);
    const virtualizedSlideInfoRef = useRef<VirtualizedSlideInfo[]>([]);
    const firstSlidePageRef = useRef<number | null>(null);
    const activeSlideIdRef = useRef<number | null>(null);
    const handleClickActionRef = useRef<((action: BlockAction, slideId: number, ev: MouseEvent) => void) | null>(null);
    const logoOverlayHostRef = useRef<HTMLDivElement | null>(null);
    const shouldRemountLogoRevealsRef = useRef<boolean>(false);
    const musicToggleButtonRef = useRef<HTMLButtonElement | null>(null);
    const isMusicMutedRef = useRef<boolean>(false);
    const fpsOverlayRef = useRef<{ stop: () => void } | null>(null);
    const hasStartedFpsOverlayRef = useRef(false);
    const lastSubmittedValuesRef = useRef<string | null>(null);
    const pendingEngineRef = useRef<CreativeEngine | null>(null);

    const config = usePlayerConfig();

    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
        activePageRef.current = activePage;
    }, [activePage]);

    useEffect(() => {
        blockActionsRef.current = blockActions;
    }, [blockActions]);

    useEffect(() => {
        activeSlideIdRef.current = activeSlideId;
    }, [activeSlideId]);

    useEffect(() => {
        return () => {
            if (slideTransitionOverlayTimeoutRef.current != null) {
                clearTimeout(slideTransitionOverlayTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const presentationTitle = presentationData?.name?.trim();
        document.title = presentationTitle || 'myiDecide';
    }, [presentationData?.name]);

    useEffect(() => {
        if (!engine) return;

        let rafId: number | null = null;
        const handleVariableChange = () => {
            if (rafId != null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const pageId = engine.scene.getCurrentPage?.();
                if (pageId) {
                    syncNamedTextBlockVariablesForScene(engine);
                    evaluateCalculatorsForPage(engine, pageId);
                }
            });
        };

        window.addEventListener(DYNAMIC_VARIABLES_UPDATED_EVENT, handleVariableChange as EventListener);
        return () => {
            window.removeEventListener(DYNAMIC_VARIABLES_UPDATED_EVENT, handleVariableChange as EventListener);
            if (rafId != null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [engine]);

    const {songs} = useSongs(MUSIC_BASE_URL);

    const startViewSession= useCallback(async (): Promise<InvitationResponse | undefined> => {
        if (!urlCode && !previewToken) {
            setError(new Error("urlCode or previewToken missing, cannot retrieve view session"));
            return undefined;
        }
        try {
            return await createSession(urlCode, previewToken);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, [urlCode, previewToken]);

    const fetchSlideData = async (): Promise<SlideInfo[] | undefined> => {
        if (!urlCode && !previewToken) {
            setError(new Error("urlCode or previewToken missing, cannot retrieve slides"));
            return undefined;
        }

        try {
            setError(null);
            const data = await getSlides(urlCode, previewToken);
            return data;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }

    const clearPlaybackTimeout = useCallback(() => {
        if (playbackTimeoutRef.current) {
            clearTimeout(playbackTimeoutRef.current);
            playbackTimeoutRef.current = null;
        }
    }, []);

    const getSlideIdForScenePage = useCallback((scenePageId?: DesignBlockId | null) => {
        if (scenePageId == null) {
            return null;
        }
        const match = virtualizedSlideInfoRef?.current?.find((slide: VirtualizedSlideInfo) => slide.scenePageId === scenePageId);
        return match?.id ?? null;
    }, [virtualizedSlideInfoRef]);

    // Helper function to format current date
    const formatCurrentDate = (): string => {
        return new Date().toLocaleString('en-US', { month: 'long', day: 'numeric' });
    };

    // Helper function to format current time
    const formatCurrentTime = (): string => {
        return new Date().toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: 'numeric' }).replace(/( AM| PM)/, '');
    };

    const replaceCustomFieldTokens = (text: string, sender?: InvitationResponse['sender'] | null): string => {
        if (!text || !sender) return text;
        let next = text;
        const businessInfo1 = sender.businessInfo1 ?? '';
        const businessInfo2 = sender.businessInfo2 ?? '';
        const businessInfo3 = sender.businessInfo3 ?? '';
        next = next.replace(/\[custom-field-1\]/g, businessInfo1);
        next = next.replace(/\[custom-field-2\]/g, businessInfo2);
        next = next.replace(/\[custom-field-3\]/g, businessInfo3);
        return next;
    };

    const resolveFinishPayloadText = (rawValue: unknown): string => {
        if (rawValue == null) {
            return '';
        }
        const text = String(rawValue);
        if (!text) {
            return '';
        }

        const senderData = sessionData?.sender;
        const viewerData = sessionData?.viewer;
        const viewerNames = splitName(viewerData?.name ?? "");

        let updatedText = text;

        if (senderData) {
            const fullName = `${senderData.firstName} ${senderData.lastName}`;
            updatedText = updatedText.replace(/\[sender-name\]/g, fullName);
            updatedText = updatedText.replace(/\[sender-name-first\]/g, senderData.firstName);
            updatedText = updatedText.replace(/\[sender-name-last\]/g, senderData.lastName);
            updatedText = updatedText.replace(/\[sender-email\]/g, senderData.email);
        } else {
            updatedText = updatedText.replace(/\[sender-name\]/g, '');
            updatedText = updatedText.replace(/\[sender-name-first\]/g, '');
            updatedText = updatedText.replace(/\[sender-name-last\]/g, '');
            updatedText = updatedText.replace(/\[sender-email\]/g, '');
        }

        if (viewerData) {
            updatedText = updatedText.replace(/\[viewer-name\]/g, viewerData.name ?? '');
            updatedText = updatedText.replace(/\[viewer-name-first\]/g, viewerNames[0]);
            updatedText = updatedText.replace(/\[viewer-name-last\]/g, viewerNames[1]);
            updatedText = updatedText.replace(/\[viewer-email\]/g, viewerData.email ?? '');
            updatedText = updatedText.replace(/\[viewer-phone\]/g, viewerData.phone ?? '');
        } else {
            updatedText = updatedText.replace(/\[viewer-name\]/g, '');
            updatedText = updatedText.replace(/\[viewer-name-first\]/g, '');
            updatedText = updatedText.replace(/\[viewer-name-last\]/g, '');
            updatedText = updatedText.replace(/\[viewer-email\]/g, '');
            updatedText = updatedText.replace(/\[viewer-phone\]/g, '');
        }

        if (sessionData?.geolocation) {
            updatedText = updatedText.replace(/\[viewer-country\]/g, sessionData.geolocation.countryName ?? '');
            updatedText = updatedText.replace(/\[viewer-region\]/g, sessionData.geolocation.regionName ?? '');
            updatedText = updatedText.replace(/\[viewer-city\]/g, sessionData.geolocation.city ?? '');
        } else {
            updatedText = updatedText.replace(/\[viewer-country\]/g, '');
            updatedText = updatedText.replace(/\[viewer-region\]/g, '');
            updatedText = updatedText.replace(/\[viewer-city\]/g, '');
        }

        updatedText = updatedText.replace(/\[current-date\]/g, formatCurrentDate());
        updatedText = updatedText.replace(/\[current-time\]/g, formatCurrentTime());
        updatedText = replaceCustomFieldTokens(updatedText, senderData);

        updatedText = updatedText.replace(/\[([^\]]+)\]/g, (match, token) => {
            const key = String(token ?? '').trim();
            if (!key) {
                return match;
            }
            const value = readDynamicVariableDisplayValue(engine, key);
            if (value !== '') {
                return value;
            }
            return match;
        });

        return updatedText;
    };

    // Helper function to replace tokens in a single text block
    const replaceTokensInTextBlock = async (engine: CreativeEngine, blockId: number): Promise<void> => {
        const TEXT_TYPE = '//ly.img.ubq/text';

        if (!sessionData?.sender)
            return;

        const senderData = sessionData?.sender;
        const viewerData = sessionData?.viewer;

        if (!engine.block.isValid(blockId)) return;
        if (engine.block.getType(blockId) !== TEXT_TYPE) return;

        try {
            const currentText = engine.block.getString(blockId, 'text/text') ?? '';
            const templateText = getCalculatorTemplateTextForBlock(engine, blockId, currentText);
            const hasDelimitedCalculator = hasDelimitedCalculatorBlocks(templateText);

            // If no tokens present, skip processing
            if (!templateText.includes('[')) return;

            // Replace tokens outside calculator blocks while leaving calculator expressions intact.
            const preservedCalculatorBlocks: string[] = [];
            const createMarker = (index: number) => `__IDECIDE_CALC_BLOCK_${index}__`;
            let updatedText = hasDelimitedCalculator
                ? replaceDelimitedCalculatorBlocks(templateText, (_expression, block, blockIndex) => {
                    preservedCalculatorBlocks[blockIndex] = block.fullMatch;
                    return createMarker(blockIndex);
                })
                : templateText;

            const fullName = `${senderData.firstName} ${senderData.lastName}`;
            updatedText = updatedText.replace(/\[sender-name\]/g, fullName);
            updatedText = updatedText.replace(/\[sender-name-first\]/g, senderData.firstName);
            updatedText = updatedText.replace(/\[sender-name-last\]/g, senderData.lastName);
            updatedText = updatedText.replace(/\[sender-email\]/g, senderData.email);

            const viewerNames = splitName(viewerData?.name ?? "");
            updatedText = updatedText.replace(/\[viewer-name\]/g, viewerData?.name ?? "");
            updatedText = updatedText.replace(/\[viewer-name-first\]/g, viewerNames[0]);
            updatedText = updatedText.replace(/\[viewer-name-last\]/g, viewerNames[1]);
            updatedText = updatedText.replace(/\[viewer-email\]/g, viewerData?.email ?? "");
            updatedText = updatedText.replace(/\[viewer-phone\]/g, viewerData?.phone ?? "");

            if (sessionData.geolocation) {
                updatedText = updatedText.replace(/\[viewer-country\]/g, sessionData.geolocation.countryName ?? "");
                updatedText = updatedText.replace(/\[viewer-region\]/g, sessionData.geolocation.regionName ?? "");
                updatedText = updatedText.replace(/\[viewer-city\]/g, sessionData.geolocation.city ?? "");
            }

            // Replace date and time tokens
            updatedText = updatedText.replace(/\[current-date\]/g, formatCurrentDate());
            updatedText = updatedText.replace(/\[current-time\]/g, formatCurrentTime());

            // Replace custom field tokens from the sender business info
            updatedText = replaceCustomFieldTokens(updatedText, senderData);

            // Replace input field variable tokens with captured values (if present)
            updatedText = updatedText.replace(/\[([^\]]+)\]/g, (match, token) => {
                const key = String(token ?? '').trim();
                if (!key) return match;
                const value = readDynamicVariableDisplayValue(engine, key);
                if (value !== '') return value;
                return match;
            });

            preservedCalculatorBlocks.forEach((blockText, index) => {
                updatedText = updatedText.split(createMarker(index)).join(blockText);
            });

            // Update the text block if changes were made
            if (updatedText !== currentText) {
                engine.block.setString(blockId, 'text/text', updatedText);
            }
        } catch (error) {
            console.error('Error replacing tokens in text block:', error);
        }
    };

    // Main function to find all text blocks in a page and replace tokens
    const replaceTokensInPage = async (engine: CreativeEngine, pageId: number): Promise<void> => {
        if (!engine.block.isValid(pageId)) return;

        // Process current block if it's a text block
        await replaceTokensInTextBlock(engine, pageId);

        // Recursively process children
        try {
            const children = engine.block.getChildren(pageId);
            for (const childId of children) {
                await replaceTokensInPage(engine, childId);
            }
        } catch (error) {
            // No children or error getting children
        }

        // Process fill if it exists
        try {
            if (engine.block.supportsFill?.(pageId)) {
                const fill = engine.block.getFill(pageId);
                if (fill) {
                    await replaceTokensInPage(engine, fill);
                }
            }
        } catch (error) {
            // No fill or error getting fill
        }
    };

    const resetPlaybackRecursive = (engine: CreativeEngine, blockId: number) => {
        if (!engine.block.isValid(blockId)) return;

        if (engine.block.supportsPlaybackTime(blockId)) {
            engine.block.setPlaying(blockId, false);
            engine.block.setPlaybackTime(blockId, 0);
            // Don't call setPlaying(true) here - let the useEffect handle starting playback
            // after the page is visible and resources are loaded
        }


        let next:number[] = [];
        try{
            const children = engine.block.getChildren(blockId);
            next = [...children];
        }catch(err: any) {
        }

        if (engine.block.supportsFill?.(blockId)) {
            const fill = engine.block.getFill(blockId);
            if (fill) next.push(fill);
        }
        next.forEach(child => resetPlaybackRecursive(engine, child));
    };

    // This function ONLY handles changing the slide and resetting its time.
    const changeSlide = useCallback(async (newPageId: DesignBlockId, justCreated: boolean) => {
        console.log('[DEBUG_LOG] changeSlide called:', { newPageId, currentIsPlaying: isPlaying });
        if (!engine || !newPageId) return;

        // Mark the next active page immediately so old slide timers stop mutating playback state.
        activePageRef.current = newPageId;

        const oldPageId: DesignBlockId | null = engine?.scene.getCurrentPage();
        console.log('[DEBUG_LOG] changeSlide oldPageId:', oldPageId);

        // Check if the new slide is a menu slide
        const resolvedSlideId = getSlideIdForScenePage(newPageId);
        const newSlide = virtualizedSlideInfoRef.current.find((slide: VirtualizedSlideInfo) => slide.id === resolvedSlideId);
        const isNavigatingToMenuSlide = newSlide?.isMenuSlide === true;

        // Check if navigating FROM a menu slide
        const oldSlide = virtualizedSlideInfoRef?.current.find((slide: VirtualizedSlideInfo) => slide.scenePageId === oldPageId);
        const isNavigatingFromMenuSlide = oldSlide?.isMenuSlide === true;

        if (oldPageId) {
            const oldPageIdPlaying = engine.block.isPlaying(oldPageId);
            console.log('stopping playback oldPageId', oldPageId, oldPageIdPlaying);
            if (oldPageIdPlaying) {
                engine.block.setPlaying(oldPageId, false);
            }
            console.log('old page paused?', engine.block.isPlaying(oldPageId));

            // Pause any global playing music (unless navigating to menu slide)
            if (oldPageId !== newPageId && !isNavigatingToMenuSlide && !isPlaying){
                console.log('active audio preview');
                MusicPlayer.pause();
            }

            // Continue showing the current slide while the load completes
        }

        // Clear completed logo reveals when changing slides
        completedLogoRevealsRef.current.clear();

        // Clear logo reveal pause flag when changing slides
        isPausedForLogoRevealRef.current = false;

        // Reset all child blocks in next page (TODO: still needed?)
        resetPlaybackRecursive(engine, newPageId);

        // Start manual sound tracking (preload sounds), if needed (before resources are loaded)
        if (SoundPlayer.shouldUse()) {
            await SoundPlayer.preloadSounds(engine, newPageId);
        }

        // Preload videos, swap low-res versions, poster images, if needed (before resources are loaded)
        if (justCreated && newSlide) {
            if (newSlide.scenePageId) {
                const manualRendering = await VideoRenderer.initSlideVideo(engine, newSlide.scenePageId, newSlide.id, newSlide.introSlide);
                await DeviceCompatibility.processSlide(engine, newPageId, !manualRendering);
            }
        }

        // Ensure all resources for the new page are loaded before making it visible
        // This helps prevent "phasing in" of elements during playback.
        try {
            await engine.block.forceLoadResources([newPageId]);
        } catch (error) {
            console.error('Failed to force load resources for slide change:', error);
        }

        const debugSlowSlideChangeDelayMs = getDebugSlowSlideChangeDelayMs();
        if (debugSlowSlideChangeDelayMs > 0) {
            console.info(`Simulating slow slide change for ${debugSlowSlideChangeDelayMs}ms via ?${DEBUG_SLOW_SLIDE_CHANGE_PARAM}.`);
            await new Promise((resolve) => window.setTimeout(resolve, debugSlowSlideChangeDelayMs));
        }

        // Show the new page (hide any the current page, if any)
        try {
            const pageIds: DesignBlockId[] = engine.scene.getPages() ?? [];
            pageIds.forEach((pageId: DesignBlockId) => {
                if (pageId !== newPageId)
                    engine.block.setVisible(pageId, false);
                engine.block.setClipped(pageId, true);
            });
        } finally {
            engine.block.setVisible(newPageId, true);
        }

        // Now that the replacement page is visible, hide the previously displayed page.
        // Leaving both pages visible causes overlay plugins to keep treating the old page
        // as active, which leaks stale HTML overlays like input fields into the new slide.
        if (oldPageId && oldPageId !== newPageId) {
            engine.block.setVisible(oldPageId, false);
        }

        // Set up new page: disable looping, remove old sounds, deselect any selections, etc.
        try {
            const audioBlocks: DesignBlockId[] = engine.block.findByType("audio") ?? [];
            audioBlocks.forEach((audioId) => {
                if (findOwningPage(engine, audioId) !== newPageId) return;
                try { engine.block.setLooping(audioId, false); } catch {}
                try { engine.block.setBool(audioId, 'audio/looping', false); } catch {}
                try { engine.block.setBool(audioId, 'playback/looping', false); } catch {}
            });
        } catch {}
        engine.block.findAllSelected().forEach(id => engine.block.setSelected(id, false));
        try { engine.block.setLooping(newPageId, false); } catch {}

        console.log('visible page', engine.scene.getCurrentPage());
        console.log('setplaybacktime changeslide', newPageId, engine.block.getPlaybackTime(newPageId));

        syncNamedTextBlockVariablesForScene(engine);
        // Replace tokens in text blocks with actual user/geolocation data
        await replaceTokensInPage(engine, newPageId);
        evaluateCalculatorsForPage(engine, newPageId);

        setActiveSlideId(resolvedSlideId ?? null);
        activeSlideIdRef.current = resolvedSlideId;

        // Reset startClicked if we are navigating back to the first slide
        const isFirstSlide = virtualizedSlideInfoRef.current.find((s: VirtualizedSlideInfo) => s.id === resolvedSlideId)?.slideIndex === 0;
        if (isFirstSlide) {
            setStartClicked(false);
        }

        console.log('changeSlide blockActions', resolvedSlideId, blockActions);
        // Defer overlay creation and resize to avoid blocking audio thread during slide change
        requestAnimationFrame(() => {
            handleResize();
            // Use another frame to ensure engine has rendered the page and bounding boxes are accurate
            requestAnimationFrame(() => {
                if (!resolvedSlideId) return;

                watermarkRef.current?.positionWatermark();

                const actionsForSlide = filterActionsForSlide(resolvedSlideId, blockActionsRef.current);
                console.log('actionsForSlide', actionsForSlide);
                createClickActionOverlayElements(actionsForSlide, {
                    container: document.getElementById('overlay-container'),
                    engine: engine,
                    activeSlideId: resolvedSlideId ?? undefined,
                    pageId: newPageId,
                    onClick: (blockId, action, slideId) => {
                        audioContext.resume();
                        handleClickActionRef.current?.(blockId, action, slideId);
                    }
                });
            });
        });

        // Set new engine pages so that we can trigger a data sync to set the new engineBlockId for each block in blockactions
        const pageIds = engine.scene.getPages();
        setPages(pageIds);

        // Update overlay visibility for the new active page (must happen regardless of play/pause state)
        updateOverlayPlugins(newPageId);

        // Resume playing on slide change
        // IMPORTANT: Clear any existing timeouts BEFORE resetting isPlaying to true
        clearPlaybackTimeout();

        // Also dispose sound player from the old slide
        soundDisposerRef.current?.();
        soundDisposerRef.current = null;

        console.log('[DEBUG_LOG] changeSlide: setting activePage to', newPageId);
        if (activePage === newPageId) {
            // If the page is the same, we need to manually trigger the playback logic
            // since the useEffect won't trigger.
            console.log('[DEBUG_LOG] changeSlide: same page, incrementing playbackNonce');
            setPlaybackNonce(prev => prev + 1);
        } else {
            setActivePage(newPageId);
        }

        // Inform server of slide play when this is a real invitation-backed session.
        if (sessionData?.viewUuid && newSlide?.slideIndex != null)
            void slideStarted(sessionData.viewUuid, newSlide.slideIndex);

        // Set isPlaying AFTER setActivePage to ensure the useEffect runs
        console.log('[DEBUG_LOG] changeSlide: setting isPlaying to true');
        setIsPlaying(true);

    }, [engine, blockActions, getSlideIdForScenePage, activePage]);

    // Control handlers ONLY manage state.
    const handlePlay = () => {
        isPausedForLogoRevealRef.current = false;
        setIsPlaying(true);
    };
    const handlePause = () => {
        isPausedForLogoRevealRef.current = false;
        setIsPlaying(false);
    };

    const buildSubmissionSignature = useCallback((values: InputFieldSubmissionValue[]): string => {
        if (!values.length) {
            return '';
        }
        return values
            .map((entry) => `${entry.name}=${entry.value ?? ''}`)
            .sort()
            .join('&');
    }, []);

    const submitSessionVariables = useCallback(async (values: InputFieldSubmissionValue[]): Promise<boolean> => {
        if (!values.length) {
            return true;
        }
        const viewUuid = sessionData?.viewUuid;
        if (!viewUuid) {
            return true;
        }
        try {
            const signature = buildSubmissionSignature(values);

            const sessionVariables: SessionVariable[] = values.map((entry) => ({
                name: entry.name,
                value: entry.value ?? '',
                localizedText: null
            }));

            const storeSuccess = await storeSessionVariables(viewUuid, sessionVariables);

            if (!storeSuccess) {
                console.error('Failed to submit input field values');
                return false;
            }
            lastSubmittedValuesRef.current = signature;
            return true;
        } catch (error) {
            console.error('Failed to submit input field values:', error);
            return false;
        }
    }, [buildSubmissionSignature, sessionData?.viewUuid]);

    const collectAllInputFieldValues = useCallback((): { inputFields: InputFieldSubmissionValue[]; choices: InputFieldSubmissionValue[] } => {
        if (!engine) return { inputFields: [], choices: [] };
        const actions = Array.isArray(blockActionsRef.current) ? blockActionsRef.current : [];
        const seenInputs = new Set<string>();
        const seenChoices = new Set<string>();
        const inputFields: InputFieldSubmissionValue[] = [];
        const choices: InputFieldSubmissionValue[] = [];

        const fieldIds = findAllInputFields(engine);
        fieldIds.forEach((fieldId) => {
            if (!engine.block.isValid?.(fieldId)) return;
            const meta = getInputFieldMeta(engine, fieldId);
            const varName = String(meta?.varName ?? '').trim();
            if (!varName || seenInputs.has(varName)) return;
            seenInputs.add(varName);

            let value = '';
            try {
                value = engine.variable?.getString?.(`input:${varName}`) ?? '';
            } catch {}
            if (!value && meta?.inputDefault) {
                value = meta.inputDefault;
            }
            inputFields.push({
                name: varName,
                value,
                label: meta?.label
            });
        });

        actions.forEach((action) => {
            const raw = action?.clickActionData?.['inputFieldOptions'];
            let meta: InputFieldMeta | null = null;
            if (typeof raw === 'string') {
                try {
                    meta = JSON.parse(raw) as InputFieldMeta;
                } catch {
                    meta = null;
                }
            } else if (raw && typeof raw === 'object') {
                meta = raw as InputFieldMeta;
            }
            const varName = String(meta?.varName ?? '').trim();
            if (!varName || seenInputs.has(varName)) return;

            let value = '';
            try {
                value = engine.variable?.getString?.(`input:${varName}`) ?? '';
            } catch {}
            if (!value && meta?.inputDefault) {
                value = meta.inputDefault;
            }
            if (!value) return;

            seenInputs.add(varName);
            inputFields.push({
                name: varName,
                value,
                label: meta?.label
            });
        });

        const clickVars = (window as any).__ClickVars;
        if (clickVars && typeof clickVars === 'object') {
            Object.keys(clickVars).forEach((key) => {
                const name = String(key ?? '').trim();
                if (!name || seenChoices.has(name)) return;
                const entry = readWindowDynamicVariableEntry(name);
                if (!entry) return;
                seenChoices.add(name);
                choices.push({
                    name,
                    value: entry.value
                });
            });
        }

        return { inputFields, choices };
    }, [engine]);

    const markPresentationFinished = useCallback(async (finishPayload?: FinishPayload): Promise<boolean> => {
        const viewUuid = sessionData?.viewUuid;
        if (!viewUuid) {
            return true;
        }

        const { inputFields, choices } = collectAllInputFieldValues();
        const inputFieldNames = new Set(inputFields.map((entry) => entry.name));
        const choiceEntries = choices.filter((entry) => !inputFieldNames.has(entry.name));
        const emailFields: InputFieldSubmissionValue[] = [...inputFields, ...choiceEntries];
        const signature = buildSubmissionSignature(emailFields);
        if (signature && signature !== lastSubmittedValuesRef.current) {
            const submitted = await submitSessionVariables(emailFields);
            if (!submitted) {
                console.warn('Proceeding with finish event despite failed session variable submission.');
            }
        }
        const fallbackActionResponse = [
            ...inputFields.map((entry) => `${entry.name}: ${entry.value ?? ''}`),
            ...choiceEntries.map((entry) => `${entry.name}: ${entry.value ?? ''}`)
        ]
            .filter((entry) => String(entry ?? '').trim() !== '')
            .join('\n');

        const fallbackActionStatus = (() => {
            const activeId = activeSlideIdRef.current;
            if (activeId != null) {
                const activeSlide = virtualizedSlideInfoRef.current.find((slide) => slide.id === activeId);
                const activeSlideName = String(activeSlide?.name ?? '').trim();
                if (activeSlideName) {
                    return activeSlideName;
                }
            }
            return String(presentationData?.name ?? '').trim();
        })();

        const actionResponse = String(finishPayload?.actionResponse ?? '').trim() || fallbackActionResponse;
        const actionResponseStatus = String(finishPayload?.actionResponseStatus ?? '').trim() || fallbackActionStatus;

        try {
            const finishedSuccess = await markFinished(
                viewUuid,
                actionResponse || '',
                actionResponseStatus || ''
            );

            if (!finishedSuccess) {
                console.error('Failed to submit finish event.');
                return false;
            }
            return true;
        } catch (error) {
            console.error('Failed to submit finish event:', error);
            return false;
        }
    }, [buildSubmissionSignature, collectAllInputFieldValues, sessionData?.viewUuid, submitSessionVariables, presentationData?.name]);

    /**
     * Load specific slides into the Creative Engine scene and track them in virtualizedSlideInfo.
     */
    const loadSlidesIntoScene = useCallback(async (engine: CreativeEngine, slidesToLoad: SlideInfo[]) => {
        if (!engine) return;

        const scene = engine.scene.get();
        if (scene === null) {
            console.error('Failed to load slides into scene, missing scene');
            return;
        }

        // 1. Mark slides as loading (update existing entries, don't append)
        const slideIdsToLoad = new Set(slidesToLoad.map(s => s.id));

        setVirtualizedSlideInfo((prev: VirtualizedSlideInfo[]) =>
            prev.map(v => slideIdsToLoad.has(v.id) ? { ...v, loading: true, loaded: false } : v)
        );
        virtualizedSlideInfoRef.current = virtualizedSlideInfoRef.current.map(v =>
            slideIdsToLoad.has(v.id) ? { ...v, loading: true, loaded: false } : v
        );

        for (const slide of slidesToLoad) {
            try {
                let pageId: DesignBlockId | undefined;

                // Load into engine (will stop playback)
                if (slide.slideData) {
                    const blocks: DesignBlockId[] = await engine.block.loadFromString(slide.slideData);

                    if (!Array.isArray(blocks) || blocks.length === 0) {
                        console.error(`Failed to load slide ${slide.id} into scene, no blocks`);
                        return;
                    }

                    pageId = blocks[0];
                }
                else {
                    // special handling for empty page, create white placeholder
                    pageId = engine.block.create("page");
                    engine.block.setWidth(pageId, PRESENTATION_WIDTH);
                    engine.block.setHeight(pageId, PRESENTATION_HEIGHT);
                    engine.block.setDuration(pageId, 2);
                }

                // Add to scene
                engine.block.appendChild(scene, pageId);

                // Initial setup
                engine.block.setVisible(pageId, false);
                engine.block.setClipped(pageId, true);

                engine.editor.setSettingBool('mouse/enableScroll', false); // Disable scrolling within the canvas element on the player
                engine.editor.setSettingColor('highlightColor', {r: 0, g: 0, b: 0, a: 0}); // Remove bounding box highlight around block when then are clicked
                engine.editor.setSettingBool('page/title/show', false); //Hide the page titles above the presentation
                engine.editor.setSettingBool('touch/singlePointPanning', false);

                // Keep camera centered on the page, no visible panning
                engine.scene.unstable_enableCameraPositionClamping(
                    [pageId],
                    0, 0, 0, 0, // paddingLeft, Top, Right, Bottom
                    0, 0, 0, 0  // scaled paddings
                );

                // Lock zoom to a fixed value (e.g. 1x)
                engine.scene.unstable_enableCameraZoomClamping(
                    [pageId],
                    1.0, // minZoomLimit
                    1.0, // maxZoomLimit
                    0, 0, 0, 0
                );

                // Update state
                setVirtualizedSlideInfo((prev: VirtualizedSlideInfo[]) =>
                    prev.map(v => v.id === slide.id ? { ...v, scenePageId: pageId, loading: false, loaded: true } : v)
                );
                virtualizedSlideInfoRef.current = virtualizedSlideInfoRef.current.map(v =>
                    v.id === slide.id ? { ...v, scenePageId: pageId, loading: false, loaded: true } : v
                );
            } catch (err) {
                console.error(`Failed to load slide ${slide.id} into scene`, err);
            }
        }

    }, [setVirtualizedSlideInfo, virtualizedSlideInfoRef]);

    /**
     * Precache the initial slides to ensure assets are in the browser cache.
     * This helps avoid interruptions when loading slides during playback.
     */
    const precacheInitialSlides = useCallback(async (engine: CreativeEngine, slidesToPreload: SlideInfo[]) => {
        if (!engine || slidesToPreload.length === 0) return;

        const startTime = performance.now();

        console.log(`Starting preplay preloader for ${slidesToPreload.length} slides`);

        for (const slide of slidesToPreload) {
            console.log(`Preloading slide ${slide.slideIndex} (ID: ${slide.id})...`);
            // create scene
            await loadSlidesIntoScene(engine, [slide]);

            const virtualSlide = virtualizedSlideInfoRef.current.find(v => v.id === slide.id);

            // audio/video changes
            if (virtualSlide?.scenePageId) {
                // preload video poster images, if needed, and prevent video assets from loading if unneeded
                // (before resources are loaded).
                const manualRendering = await VideoRenderer.initSlideVideo(engine, virtualSlide.scenePageId, virtualSlide.id, virtualSlide.introSlide);
                await DeviceCompatibility.processSlide(engine, virtualSlide.scenePageId, !manualRendering);

                // remove sounds from intro slide (on mobile, causes issues with videos' audio playback later)
                if (virtualSlide.introSlide && SoundPlayer.shouldUse()) {
                    SoundPlayer.removeSoundsFromSlide(engine, virtualSlide.scenePageId);
                }
            }

            // After loading the first (intro) slide, calculate and set the aspect ratio
            if (slide.slideIndex === 0) {
                if (virtualSlide?.scenePageId) {
                    try {
                        // Get the canvas element to read its actual rendered dimensions
                        const canvasElement = engine.element?.querySelector('canvas');
                        if (canvasElement) {
                            const canvasWidth = canvasElement.clientWidth;
                            const canvasHeight = canvasElement.clientHeight;
                            if (canvasWidth && canvasHeight) {
                                const aspectRatio = canvasWidth / canvasHeight;
                                setLoadingOverlayAspectRatio(aspectRatio);
                                console.log(`First slide aspect ratio set from canvas: ${aspectRatio} (${canvasWidth}x${canvasHeight})`);
                            }
                        } else {
                            // Fallback to page block dimensions if canvas not available
                            const pageWidth = engine.block.getWidth(virtualSlide.scenePageId);
                            const pageHeight = engine.block.getHeight(virtualSlide.scenePageId);
                            if (pageWidth && pageHeight) {
                                const aspectRatio = pageWidth / pageHeight;
                                setLoadingOverlayAspectRatio(aspectRatio);
                                console.log(`First slide aspect ratio set from page block: ${aspectRatio} (${pageWidth}x${pageHeight})`);
                            }
                        }
                    } catch (err) {
                        console.error('Failed to get first slide dimensions:', err);
                    }
                }
                // Show loading overlay after first slide is loaded and aspect ratio is calculated
                setLoadingOverlayVisible(true);
            }

            // Find the virtual slide we just loaded to get its scenePageId
            if (virtualSlide?.scenePageId) {
                console.log(`Forcing resource load for slide ${slide.slideIndex}...`);
                await engine.block.forceLoadResources([virtualSlide.scenePageId]);
                console.log(`Resources loaded for slide ${slide.slideIndex}.`);

                // Remove from stage if it's not one of the first 3 slides
                if (slide.slideIndex === undefined || slide.slideIndex >= 3) {
                    console.log(`Removing slide ${slide.slideIndex} from stage after preloading.`);

                    VideoRenderer.clearSlide(virtualSlide.id);
                    engine.block.destroy(virtualSlide.scenePageId);
                    // Update virtualizedSlideInfo so it doesn't think it's still loaded in the scene
                    setVirtualizedSlideInfo((prev: VirtualizedSlideInfo[]) =>
                        prev.map(v => v.id === slide.id ? { ...v, scenePageId: undefined, loaded: false } : v)
                    );
                    virtualizedSlideInfoRef.current = virtualizedSlideInfoRef.current.map(v =>
                        v.id === slide.id ? { ...v, scenePageId: undefined, loaded: false } : v
                    );
                }
            }
        }

        const endTime = performance.now();
        const durationSeconds = ((endTime - startTime) / 1000).toFixed(2);
        console.log(`Preplay preloader finished. Took ${durationSeconds} seconds.`);
        setLoadingOverlayVisible(false);
    }, [loadSlidesIntoScene, setLoadingOverlayVisible, setVirtualizedSlideInfo, virtualizedSlideInfoRef, setLoadingOverlayAspectRatio]);

    /**
     * Remove specific slides from the Creative Engine scene and the virtualization tracker.
     */
    const unloadSlidesFromScene = useCallback(async (engine: CreativeEngine, slidesToUnload: VirtualizedSlideInfo[]) => {
        if (!engine) return;

        for (const v of slidesToUnload) {
            if (v.scenePageId && engine.block.isValid(v.scenePageId)) {
                try {
                    engine.block.destroy(v.scenePageId);
                } catch (err) {
                    console.error(`Failed to destroy block ${v.scenePageId} for slide ${v.id}`, err);
                }
            }
        }

        const idsToRemove = new Set(slidesToUnload.map(v => v.id));
        setVirtualizedSlideInfo((prev: VirtualizedSlideInfo[]) => prev.filter(v => !idsToRemove.has(v.id)));
        virtualizedSlideInfoRef.current = virtualizedSlideInfoRef.current.filter(v => !idsToRemove.has(v.id));
    }, [setVirtualizedSlideInfo, virtualizedSlideInfoRef]);

    const handleRestart = async () => {
        // Don't set isPlaying to false here - let changeSlide handle it
        // setIsPlaying(false);
        setStartClicked(false);
        clearPlaybackTimeout();
        completedLogoRevealsRef.current.clear();
        isPausedForLogoRevealRef.current = false;
        shouldRemountLogoRevealsRef.current = true;

        if (!virtualizedSlideInfoRef.current) return;

        // Find the first slide from the source of truth
        const firstSlide = virtualizedSlideInfoRef.current.find(s => s.slideIndex === 0);
        if (firstSlide?.id) {
            console.log('[DEBUG_LOG] handleRestart: navigating to first slide ID', firstSlide.id);
            await navigateToSlide(firstSlide.id);
        } else {
            console.warn('[DEBUG_LOG] handleRestart: first slide not found in virtualizedSlideInfo');
        }
    };

    /**
     * Purge slides from the engine that are not needed for the current slide.
     * Keeps only:
     * - The current slide, if set (currentSlideId)
     * - The target/next slide (targetSlideId)
     * - The next slide if current slide has auto-advance enabled
     * - Any slides referenced in block actions on the current slide
     */
    const purgeSlides = useCallback(async (targetSlideId: number, currentSlideId: number | null) => {
        if (!engine || !virtualizedSlideInfoRef.current) return;

        const currentSlide = virtualizedSlideInfoRef.current.find((s: VirtualizedSlideInfo) => s.id === targetSlideId);
        if (!currentSlide) return;

        const neededSlideIds = new Set<number>();

        // Always keep the current slide
        neededSlideIds.add(targetSlideId);

        if (currentSlideId)
            neededSlideIds.add(currentSlideId);

        // Add auto-advance target (next slide)
        if (currentSlide.autoAdvanceSlideId) {
            const autoId = Number(currentSlide.autoAdvanceSlideId);
            if (!isNaN(autoId)) {
                neededSlideIds.add(autoId);
            }
        }

        // Add all slides referenced in block actions for this slide
        const actions = filterActionsForSlide(currentSlide.id, blockActionsRef.current);
        if (!isMobile()) { // mobile memory savings, and so video-render doesn't have to recreate videos
            for (const action of actions) {
                if (action.clickActionTarget && Number.isFinite(Number(action.clickActionTarget)))
                    neededSlideIds.add(Number(action.clickActionTarget));
            }
        }

        // Find slides to remove (not in needed set)
        const currentlyLoaded = virtualizedSlideInfoRef.current.filter(v =>
            v.loaded && v.scenePageId
        );
        const slidesToPurge = currentlyLoaded.filter(v =>
            !neededSlideIds.has(v.id!) && v.scenePageId !== undefined
        );

        // Destroy pages from engine and update VirtualizedSlideInfo
        for (const v of slidesToPurge) {
            if (v.scenePageId && engine.block.isValid(v.scenePageId)) {
                try {
                    console.log(`Purging slide ${v.id} (scenePageId: ${v.scenePageId})`);
                    engine.block.destroy(v.scenePageId);
                } catch (err) {
                    console.error(`Failed to destroy block ${v.scenePageId} for slide ${v.id}`, err);
                }
            }
        }

        // Update virtualized slide info for purged slides
        setVirtualizedSlideInfo((prev: VirtualizedSlideInfo[]) =>
            prev.map(v => {
                if (slidesToPurge.some(purged => purged.id === v.id)) {
                    return {
                        ...v,
                        loading: false,
                        loaded: false,
                        scenePageId: undefined
                    };
                }
                return v;
            })
        );

        virtualizedSlideInfoRef.current = virtualizedSlideInfoRef.current.map(v => {
            if (slidesToPurge.some(purged => purged.id === v.id)) {
                return {
                    ...v,
                    loading: false,
                    loaded: false,
                    scenePageId: undefined
                };
            }
            return v;
        });

        if (slidesToPurge.length > 0) {
            console.log(`Purged ${slidesToPurge.length} slides:`, slidesToPurge.map(s => s.id));
        }
    }, [engine, setVirtualizedSlideInfo, virtualizedSlideInfoRef, blockActionsRef]);

    /**
     * Predictive loading: determines which slides might be needed next (based on auto-advance
     * and block actions) and ensures they are loaded into the engine.
     */
    const prefetchFutureSlides = useCallback(async (currentSlideId: number, maxToLoad?: number) => {
        if (!engine || !virtualizedSlideInfoRef.current) return;

        const currentSlide = virtualizedSlideInfoRef.current.find((s: VirtualizedSlideInfo) => s.id === currentSlideId);
        if (!currentSlide) return;

        const neededSlideIds = new Set<number>();

        // 1. Add auto-advance target
        if (currentSlide.autoAdvanceSlideId) {
            const autoId = Number(currentSlide.autoAdvanceSlideId);
            if (!isNaN(autoId)) neededSlideIds.add(autoId);
        }

        // 2. Add all slides referenced in block actions for this slide
        const actions = filterActionsForSlide(currentSlide.id, blockActionsRef.current);
        for (const action of actions) {
            if (action.clickActionTarget && Number.isFinite(Number(action.clickActionTarget)))
                neededSlideIds.add(Number(action.clickActionTarget));
        }

        // 3. Add adjacent slides (classic virtualization)
        const currentIndex = currentSlide.slideIndex ?? 0;
        const slides = virtualizedSlideInfoRef.current;
        if (currentIndex > 0) neededSlideIds.add(slides[currentIndex - 1].id!);
        if (currentIndex < slides.length - 1) neededSlideIds.add(slides[currentIndex + 1].id!);

        // 4. Determine which of these aren't loaded yet
        const currentlyLoaded = virtualizedSlideInfoRef.current;
        let slidesToLoad: VirtualizedSlideInfo[] = virtualizedSlideInfoRef.current.filter((s: VirtualizedSlideInfo) =>
            neededSlideIds.has(s.id!) && !currentlyLoaded.some(v => v.id === s.id)
        );

        // Apply optional limit
        if (maxToLoad !== undefined && slidesToLoad.length > maxToLoad) {
            slidesToLoad = slidesToLoad.slice(0, maxToLoad);
        }

        if (slidesToLoad.length > 0) {
            console.log('Prefetching slides:', slidesToLoad.map(s => s.id));
            await loadSlidesIntoScene(engine, slidesToLoad);
        }

        // 5. Clean up slides that are no longer in our "needed" set
        // IMPORTANT: Explicitly ensure currentSlideId is never purged here
        const slidesToUnload = currentlyLoaded.filter(v =>
            !neededSlideIds.has(v.id!) && v.id !== currentSlideId && !v.isMenuSlide
        );

        if (slidesToUnload.length > 0) {
            console.log('Unloading old slides:', slidesToUnload.map(s => s.id));
            await unloadSlidesFromScene(engine, slidesToUnload);
        }
    }, [engine, loadSlidesIntoScene, unloadSlidesFromScene, isPlaying]);

    const getOrderedSlides = useCallback((): VirtualizedSlideInfo[] => {
        const slides = virtualizedSlideInfoRef.current ?? [];
        if (slides.length === 0) return [];
        const hasIndexes = slides.every(s => typeof s.slideIndex === 'number');
        if (!hasIndexes) return slides;
        return [...slides].sort((a, b) => (a.slideIndex ?? 0) - (b.slideIndex ?? 0));
    }, []);

    /**
     * Main entry point for navigating to a slide by its database ID.
     * It ensures the slide is loaded, performs the switch, and then
     * cleans up and prefetches.
     */
    const clearPendingAutoAdvance = useCallback(() => {
        if (pendingAutoAdvanceIntervalRef.current != null) {
            clearInterval(pendingAutoAdvanceIntervalRef.current);
            pendingAutoAdvanceIntervalRef.current = null;
        }
        if (pendingAutoAdvanceTimeoutRef.current != null) {
            clearTimeout(pendingAutoAdvanceTimeoutRef.current);
            pendingAutoAdvanceTimeoutRef.current = null;
        }
        pendingAutoAdvanceRef.current = null;
    }, []);

    const navigateToSlide = useCallback(async (targetSlideId: number): Promise<boolean> => {
        if (!engine || !virtualizedSlideInfoRef.current) return false;
        if (slideTransitionLockRef.current) return false;

        const currentSlideId = activeSlideIdRef.current;
        if (currentSlideId === targetSlideId) return false;
        slideTransitionLockRef.current = true;
        const slides = virtualizedSlideInfoRef.current;
        const currentSlide = currentSlideId != null
            ? slides.find((s: VirtualizedSlideInfo) => s.id === currentSlideId)
            : undefined;
        let navigationSucceeded = false;

        try {
            // Validate any required inputs. Prevent slide change if invalid.
            if (currentSlideId != null) {
                const targetSlide = slides.find((s: VirtualizedSlideInfo) => s.id === targetSlideId);
                if (currentSlide && targetSlide) {
                    const orderedSlides = getOrderedSlides();
                    const currentIndex = orderedSlides.findIndex(s => s.id === currentSlideId);
                    const targetIndex = orderedSlides.findIndex(s => s.id === targetSlideId);
                    const isForward = currentIndex !== -1 && targetIndex !== -1 && targetIndex > currentIndex;
                    const isNavigatingToMenuSlide = targetSlide.isMenuSlide === true;
                    if (isForward && !isNavigatingToMenuSlide) {
                        const pageId = engine.scene?.getCurrentPage?.();
                        if (pageId) {
                            const tokenContext = sessionData?.sender
                                ? {
                                    user: {
                                        firstName: sessionData.sender.firstName,
                                        lastName: sessionData.sender.lastName,
                                        email: sessionData.sender.email
                                    },
                                    geolocation: sessionData.geolocation
                                        ? {
                                            countryName: sessionData.geolocation.countryName,
                                            regionName: sessionData.geolocation.regionName,
                                            city: sessionData.geolocation.city
                                        }
                                        : undefined
                                }
                                : undefined;
                            const valid = await validateRequiredInputFields(engine, pageId, { replaceTokens: true, tokenContext });
                            if (!valid) {
                                return false;
                            }
                        }
                    }
                }
            }

            beginSlideTransition();

            // Get the target slide
            let targetSlide = virtualizedSlideInfoRef.current.find((s: VirtualizedSlideInfo) => s.id === targetSlideId);

            // Stop music if returning to the intro slide (index 0) or a muted slide
            if (targetSlide?.slideIndex === 0 || targetSlide?.muteBackgroundMusic === true) {
                MusicPlayer.pause();
            }

            // If not loaded (or paged out), we must load it immediately before switching
            let createdPage:boolean = false;
            if (!targetSlide?.scenePageId) {
                if (targetSlide) {
                    console.log('Target slide not paged in. Loading immediately:', targetSlideId);
                    await loadSlidesIntoScene(engine, [targetSlide]);
                    createdPage = true;

                    // Reload the target slide from the latest virtualization state
                    targetSlide = virtualizedSlideInfoRef.current.find((s) => s.id === targetSlideId);
                }
            }

            if (targetSlide?.scenePageId) {
                // Remove existing slides (except for the target and currently displayed slide)
                await purgeSlides(targetSlideId, currentSlideId);
                clearPendingAutoAdvance();

                // Remove the currently displayed slide's videos before switching.
                if (currentSlideId) {
                    VideoRenderer.clearSlide(currentSlideId, targetSlide.scenePageId === currentSlide?.scenePageId);
                }

                // Switch to the page
                await changeSlide(targetSlide.scenePageId, createdPage);
                navigationSucceeded = true;
            }
            else {
                console.warn('Failed to navigate to slide:', targetSlideId);
            }

            // Remove other slides (including the now-previous slide)
            await purgeSlides(targetSlideId, null);
            return navigationSucceeded;
        } finally {
            slideTransitionLockRef.current = false;
            endSlideTransition();
        }
    }, [beginSlideTransition, changeSlide, clearPendingAutoAdvance, endSlideTransition, engine, getOrderedSlides, loadSlidesIntoScene, purgeSlides, sessionData?.geolocation, sessionData?.sender]);

    const findNextNonMenuSlide = useCallback((fromSlideId: number, direction: 1 | -1): SlideInfo | null => {
        const orderedSlides = getOrderedSlides();
        const currentIndex = orderedSlides.findIndex(slide => slide.id === fromSlideId);
        if (currentIndex === -1) return null;

        for (let i = currentIndex + direction; i >= 0 && i < orderedSlides.length; i += direction) {
            const candidate = orderedSlides[i];
            if (candidate && candidate.isMenuSlide !== true) {
                return candidate;
            }
        }

        return null;
    }, [getOrderedSlides]);

    const resolveAutoAdvanceTarget = useCallback((slideToGo: string | undefined, fromSlideId: number): SlideInfo | null => {
        if (!slideToGo) return null;

        if (slideToGo === 'next-slide') {
            return findNextNonMenuSlide(fromSlideId, 1);
        }

        if (slideToGo === 'previous-slide') {
            return findNextNonMenuSlide(fromSlideId, -1);
        }

        const targetSlideId = Number(slideToGo);
        if (!Number.isFinite(targetSlideId)) return null;

        const orderedSlides = getOrderedSlides();
        const targetSlide = orderedSlides.find(slide => slide.id === targetSlideId);
        if (!targetSlide) {
            // Target slide may have been deleted after this auto-advance was configured.
            return findNextNonMenuSlide(fromSlideId, 1);
        }

        if (targetSlide.isMenuSlide !== true) {
            return targetSlide;
        }

        const fromSlide = orderedSlides.find(slide => slide.id === fromSlideId);
        const fromIndex = fromSlide ? orderedSlides.indexOf(fromSlide) : -1;
        const targetIndex = orderedSlides.indexOf(targetSlide);
        const direction = fromIndex >= 0 && targetIndex >= 0 && targetIndex < fromIndex ? -1 : 1;
        return findNextNonMenuSlide(targetSlideId, direction);
    }, [findNextNonMenuSlide, getOrderedSlides]);

    const handleNext = async (): Promise<boolean> => {
        console.log('handleNext', activeSlideId, activeSlideIdRef);
        if (!virtualizedSlideInfoRef.current || !activeSlideIdRef?.current) return false;

        const currentSlide = virtualizedSlideInfoRef.current.find(s => s.id === activeSlideIdRef?.current);
        if (!currentSlide) return false;

        // If we're on the first slide and haven't started yet, call handleStartPlayerEngine instead
        if (currentSlide.slideIndex === 0 && !startClicked) {
            return handleStartPlayerEngine();
        }

        const pageId = engine?.scene?.getCurrentPage?.();
        if (engine && pageId) {
            const tokenContext = sessionData?.sender
                ? {
                    user: {
                        firstName: sessionData.sender.firstName,
                        lastName: sessionData.sender.lastName,
                        email: sessionData.sender.email
                    },
                    geolocation: sessionData.geolocation
                        ? {
                            countryName: sessionData.geolocation.countryName,
                            regionName: sessionData.geolocation.regionName,
                            city: sessionData.geolocation.city
                        }
                        : undefined
                }
                : undefined;
            const valid = await validateRequiredInputFields(engine, pageId, { replaceTokens: true, tokenContext });
            if (!valid) {
                return false;
            }
        }

        const nextSlide = findNextNonMenuSlide(currentSlide.id, 1);
        if (nextSlide?.id) {
            return navigateToSlide(nextSlide.id);
        }
        return false;
    };

    const schedulePendingAutoAdvance = useCallback((slideId: number, targetSlideId: number) => {
        pendingAutoAdvanceRef.current = { slideId, targetSlideId };
        if (pendingAutoAdvanceIntervalRef.current != null) {
            return;
        }

        pendingAutoAdvanceIntervalRef.current = window.setInterval(async () => {
            if (!engine) return;
            const pending = pendingAutoAdvanceRef.current;
            if (!pending) {
                clearPendingAutoAdvance();
                return;
            }
            if (activeSlideIdRef.current !== pending.slideId) {
                clearPendingAutoAdvance();
                return;
            }

            const pageId = engine.scene?.getCurrentPage?.();
            if (!pageId) return;
            const tokenContext = sessionData?.sender
                ? {
                    user: {
                        firstName: sessionData.sender.firstName,
                        lastName: sessionData.sender.lastName,
                        email: sessionData.sender.email
                    },
                    geolocation: sessionData.geolocation
                        ? {
                            countryName: sessionData.geolocation.countryName,
                            regionName: sessionData.geolocation.regionName,
                            city: sessionData.geolocation.city
                        }
                        : undefined
                }
                : undefined;
            const valid = await validateRequiredInputFields(engine, pageId, { replaceTokens: true, tokenContext });
            if (!valid) return;

            if (pendingAutoAdvanceIntervalRef.current != null) {
                clearInterval(pendingAutoAdvanceIntervalRef.current);
                pendingAutoAdvanceIntervalRef.current = null;
            }

            pendingAutoAdvanceTimeoutRef.current = window.setTimeout(async () => {
                const latest = pendingAutoAdvanceRef.current;
                if (!latest || latest.slideId !== pending.slideId) {
                    clearPendingAutoAdvance();
                    return;
                }
                const currentPageId = engine.scene?.getCurrentPage?.();
                if (!currentPageId) {
                    clearPendingAutoAdvance();
                    return;
                }
                const stillValid = await validateRequiredInputFields(engine, currentPageId, { replaceTokens: true, tokenContext });
                if (!stillValid) {
                    return;
                }
                clearPendingAutoAdvance();
                await navigateToSlide(pending.targetSlideId);
            }, 1000);
        }, 250);
    }, [engine, navigateToSlide, presentationData, clearPendingAutoAdvance]);

    const handlePrevious = async (): Promise<boolean> => {
        if (!virtualizedSlideInfoRef.current || !activeSlideIdRef?.current) return false;

        const currentSlide = virtualizedSlideInfoRef.current.find(s => s.id === activeSlideIdRef?.current);
        if (!currentSlide) return false;

        const previousSlide = findNextNonMenuSlide(currentSlide.id, -1);
        if (previousSlide?.id) {
            return navigateToSlide(previousSlide.id);
        }
        return false;
    };

    const handleLogoRevealComplete = useCallback((blockId: number) => {
        if (!engine) return;
        console.log('logo reveal onComplete called');
        // Mark this logo reveal as completed
        completedLogoRevealsRef.current.add(blockId);

        const resumedFromLogoRevealPause = isPausedForLogoRevealRef.current;
        if (!resumedFromLogoRevealPause) {
            shouldRemountLogoRevealsRef.current = true;
            return;
        }

        const currentPageId = engine.scene.getCurrentPage();
        if (!currentPageId) {
            return;
        }

        // Find all logo reveal blocks on the current page
        const allLogoReveals = findAllLogoReveals(engine);
        const logoRevealsOnCurrentPage = allLogoReveals.filter((logoBlockId) => {
            const ownerPage = findOwningPage(engine, logoBlockId);
            return ownerPage === currentPageId;
        });

        // Check if any logo reveal on this page has pauseWhileActive set to true
        const shouldResume = logoRevealsOnCurrentPage.some((logoBlockId) => {
            const meta = getLogoRevealMeta(engine, logoBlockId);
            return meta.pauseWhileActive === true;
        });
        console.log('shouldResume', shouldResume);
        console.log('isPlaying', isPlayingRef.current);

        // If pauseWhileActive is true and playback should be running, resume playback
        if (shouldResume && isPlayingRef.current) {
            isPausedForLogoRevealRef.current = false;
            engine.block.setPlaying(currentPageId, true);
        }

        const timeToChangeSlide = Math.max(0, remainingSlideTimeRef.current - delayUntilVisibleRef.current);
        const currentVirtualizedSlide = virtualizedSlideInfoRef?.current.find((slide: VirtualizedSlideInfo) => slide.scenePageId == currentPageId);
        const isMenuSlide = currentVirtualizedSlide?.isMenuSlide === true;
        console.log('timeToChangeSlide', timeToChangeSlide);
        console.log('currentVirtualizedSlide', currentVirtualizedSlide);
        if (isMenuSlide) {
            // Menu slides should not auto-advance
            return;
        }

        clearPlaybackTimeout();
        playbackTimeoutRef.current = window.setTimeout(async () => {
            // When time is up, advance to the next slide if the "autoadvanceafternarration" flag is set to true

                if (currentVirtualizedSlide?.autoAdvanceAfterNarration == true) {
                    const slideToGo: string | undefined = currentVirtualizedSlide.autoAdvanceSlideId;
                    if (slideToGo && slideToGo !== '') {
                        const fromSlideId = currentVirtualizedSlide?.id ?? activeSlideIdRef.current;
                        if (fromSlideId != null) {
                            const targetSlide = resolveAutoAdvanceTarget(slideToGo, fromSlideId);
                            if (targetSlide?.id) {
                                const pageId = engine?.scene?.getCurrentPage?.();
                                if (engine && pageId) {
                                    const tokenContext = sessionData?.sender
                                        ? {
                                            user: {
                                                firstName: sessionData.sender.firstName,
                                                lastName: sessionData.sender.lastName,
                                                email: sessionData.sender.email
                                            },
                                            geolocation: sessionData.geolocation
                                                ? {
                                                    countryName: sessionData.geolocation.countryName,
                                                    regionName: sessionData.geolocation.regionName,
                                                    city: sessionData.geolocation.city
                                                }
                                                : undefined
                                        }
                                        : undefined;
                                    const valid = await validateRequiredInputFields(engine, pageId, { replaceTokens: true, tokenContext });
                                    if (!valid) {
                                        schedulePendingAutoAdvance(fromSlideId, targetSlide.id);
                                        return;
                                    }
                                }
                                await navigateToSlide(targetSlide.id);
                            }
                        }
                    }
                }
        }, timeToChangeSlide);

        // Force logo overlays to be rebuilt after we leave this slide so they reappear when returning
        shouldRemountLogoRevealsRef.current = true;
    }, [engine, navigateToSlide, resolveAutoAdvanceTarget, schedulePendingAutoAdvance, presentationData, clearPlaybackTimeout]);

    const remountLogoRevealOverlays:any = useCallback(() => {
        if (!engine) return;
        const overlayContainer = document.getElementById('overlay-container');
        if (!overlayContainer) return;

        try {
            logoRevealControllerRef.current?.cleanup();
        } catch {}
        logoRevealControllerRef.current = null;

        if (logoOverlayHostRef.current?.parentElement) {
            logoOverlayHostRef.current.remove();
        }
        const host = document.createElement('div');
        Object.assign(host.style, {
            position: 'absolute',
            inset: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
        });
        host.setAttribute('data-role', 'logo-reveal-host');
        logoOverlayHostRef.current = host;
        overlayContainer.appendChild(host);

        logoRevealControllerRef.current = mountLogoRevealOverlays(engine, {
            container: host,
            interactive: true,
            clearHTMLOverlays: true,
            onComplete: handleLogoRevealComplete
        });

        // Always move logo overlays to the end so they sit on top of other overlays
        const moveHostToTop = () => {
            if (logoOverlayHostRef.current?.parentElement === overlayContainer) {
                overlayContainer.appendChild(logoOverlayHostRef.current);
            }
        };
        requestAnimationFrame(moveHostToTop);
        requestAnimationFrame(moveHostToTop);
    }, [engine, handleLogoRevealComplete]);

    useEffect(() => {
        if (shouldRemountLogoRevealsRef.current) {
            remountLogoRevealOverlays();
            shouldRemountLogoRevealsRef.current = false;
        }
    }, [activePage, playbackNonce, remountLogoRevealOverlays]);

    const getOverlayContainer = () => {
        let el = document.getElementById('overlay-container');
        if (el) return el;
        const host = document.getElementById("root-shadow");
        const root = host?.shadowRoot || (host as any)?.__shadowRoot;
        return root?.getElementById('overlay-container');
    };

    const positionMusicToggleButton = (pageId: DesignBlockId, button: HTMLButtonElement, host: HTMLElement) => {
        const canvas = engine?.element as HTMLCanvasElement | undefined;
        if (!canvas) {
            button.style.display = 'none';
            return;
        }

        const raw = engine?.block.getScreenSpaceBoundingBoxXYWH([pageId]);
        if (!raw) {
            button.style.display = 'none';
            return;
        }

        const pageX = raw[0];
        const pageY = raw[1];
        const pageWidth = raw[2];
        const pageHeight = raw[3];

        const hostRect = host.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();

        const pageLeft = (canvasRect.left + pageX) - hostRect.left;
        const pageTop = (canvasRect.top + pageY) - hostRect.top;

        const scaleFactor = pageWidth / PRESENTATION_WIDTH;
        const baseWidth = 120;
        const baseHeight = 36;
        const baseRadius = 18;
        const baseFontSize = 14;
        const baseOffset = 26;

        const width = baseWidth * scaleFactor;
        const height = baseHeight * scaleFactor;
        const radius = baseRadius * scaleFactor;
        const fontSize = baseFontSize * scaleFactor;
        const offset = baseOffset * scaleFactor;

        button.style.width = `${width}px`;
        button.style.height = `${height}px`;
        button.style.borderRadius = `${radius}px`;
        button.style.fontSize = `${fontSize}px`;

        const left = pageLeft + pageWidth - width - offset;
        const top = pageTop + pageHeight - height - offset;
        button.style.left = `${left}px`;
        button.style.top = `${top}px`;
        button.style.display = 'flex';
    };

    const updateBackgroundMusicMix = useCallback((pageId?: number | null) => {
        if (!engine || !MusicPlayer.hasAudio()) return;

        const targetPageId = pageId ?? activePage;
        if (!targetPageId) return;

        const targetVirtualizedSlide = virtualizedSlideInfoRef?.current.find(
            (slide: VirtualizedSlideInfo) => slide.scenePageId == targetPageId,
        );
        const isMenuSlide = targetVirtualizedSlide?.isMenuSlide === true;
        const targetVolume = isMenuSlide ? DUCKED_MUSIC_VOLUME : BASE_MUSIC_VOLUME;
        if (MusicPlayer.getVolume() !== targetVolume) {
            MusicPlayer.setVolume(targetVolume);
        }
    }, [activePage, engine]);

    const refreshMusicToggleButton = useCallback(() => {
        if (!engine) return;
        const host = getOverlayContainer();
        if (!host) return;

        const hasGlobalMusic = !!selectedSong && selectedSong !== 'none';
        if (!hasGlobalMusic) {
            musicToggleButtonRef.current?.remove();
            musicToggleButtonRef.current = null;
            return;
        }

        const currentPageId = activePage;
        if (!currentPageId) {
            musicToggleButtonRef.current?.remove();
            musicToggleButtonRef.current = null;
            return;
        }

        const currentVirtualizedSlide = virtualizedSlideInfoRef?.current.find((slide: VirtualizedSlideInfo) => slide.scenePageId == currentPageId);
        const isMenuSlide = currentVirtualizedSlide?.isMenuSlide === true;

        if (!isMenuSlide) {
            musicToggleButtonRef.current?.remove();
            musicToggleButtonRef.current = null;
            return;
        }

        let button = musicToggleButtonRef.current;
        if (!button) {
            button = document.createElement('button');
            button.type = 'button';
            button.style.cssText = `
                position: absolute;
                z-index: 9999;
                background-color: rgba(0, 0, 0, 0.75);
                color: #fff;
                border: 1px solid rgba(255, 255, 255, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                pointer-events: auto;
                padding: 0;
                transition: transform 0.2s ease, box-shadow 0.2s ease;
            `;
            button.addEventListener('mouseenter', () => {
                button!.style.transform = 'scale(1.05)';
                button!.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            });
            button.addEventListener('mouseleave', () => {
                button!.style.transform = 'scale(1)';
                button!.style.boxShadow = 'none';
            });
            button.addEventListener('click', () => {
                if (!MusicPlayer.hasAudio()) return;
                if (MusicPlayer.isPlaying()) {
                    MusicPlayer.pause();
                    isMusicMutedRef.current = true;
                } else {
                    MusicPlayer.unPause();
                    isMusicMutedRef.current = false;
                }
                updateBackgroundMusicMix();
                refreshMusicToggleButton();
            });
            host.appendChild(button);
            musicToggleButtonRef.current = button;
        }

        const isPlaying = MusicPlayer.isPlaying() && !isMusicMutedRef.current;
        button.textContent = isPlaying ? 'Music On' : 'Music Off';
        positionMusicToggleButton(currentPageId, button, host);
    }, [engine, activePage, updateBackgroundMusicMix, selectedSong]);

    const handleStartPlayerEngine = async (): Promise<boolean> => {
        const previouslyStarted = hasStarted;
        setStartClicked(true);

        if (!previouslyStarted)
            await VideoRenderer.firstInteraction();

        // create audio context for audio playback engine
        AudioContext.init();

        // Inform server of start (don't wait for response)
        if (!hasStarted) {
            if (sessionData?.viewUuid)
                void markStarted(sessionData.viewUuid);
            setHasStarted(true);
        }

        // Start presentation-wide music
        if (selectedSong != 'none') {
            const curatedSong = songs?.find((s: any) => s.id === selectedSong);
            const musicUrl = selectedMusicUrl ?? (curatedSong?.directory ? `${MUSIC_BASE_URL}/${curatedSong.directory}/loop.mp3` : null);
            if (musicUrl) {
                try {
                    await MusicPlayer.init(musicUrl, 0.15);
                    isMusicMutedRef.current = false;
                } catch (err) {
                    console.warn('[DEBUG_LOG] Failed to init background music, continuing without it.', err);
                }
            } else {
                console.warn('[DEBUG_LOG] Selected song not found; skipping music init.', { selectedSong });
            }
            updateBackgroundMusicMix(activePage);
            refreshMusicToggleButton();
        }


        // Go to next slide (navigate directly to avoid circular call with handleNext)
        if (!virtualizedSlideInfoRef.current || !activeSlideIdRef?.current) return false;

        const currentSlide = virtualizedSlideInfoRef.current.find(s => s.id === activeSlideIdRef?.current);
        if (!currentSlide) return false;

        const nextSlide = findNextNonMenuSlide(currentSlide.id, 1);
        if (nextSlide?.id) {
            return navigateToSlide(nextSlide.id);
        }
        return false;
    }

    const handleClickAction = useCallback(async (action: BlockAction, slideId: number, ev: MouseEvent) => {
        const recordTrackedChoice = (entry: BlockAction) => {
            const clickData = entry?.clickActionData;
            if (!clickData?.store) return;
            const varName = String(clickData?.varName ?? '').trim();
            if (!varName) return;

            let value = String(clickData?.value ?? '').trim();
            if (!value) {
                if (entry?.clickActionType === 'openLink') {
                    value = String(clickData?.['url'] ?? '').trim();
                } else if (entry?.clickActionType === 'goToSlide') {
                    value = String(entry?.clickActionTarget ?? '').trim();
                }
            }
            if (!value) {
                value = entry?.clickActionType ?? 'choice';
            }

            writeChoiceVariable(engine, varName, value, clickData?.valueType);
        };

        switch(action.clickActionType) {
            case "goToSlide":
            {
                recordTrackedChoice(action);
                if (action.clickActionTarget === 'nextSlide'){
                    await handleNext();
                    break;
                }
                if (action.clickActionTarget === 'previousSlide') {
                    console.log('go to previous');
                    await handlePrevious();
                    break;
                }

                const newSlideId = Number(action.clickActionTarget);
                await navigateToSlide(newSlideId);
                break;
            }
            case 'openLink':
            {
                recordTrackedChoice(action);
                const currentPageId = engine?.scene?.getCurrentPage?.();
                if (engine && currentPageId) {
                    syncNamedTextBlockVariablesForScene(engine);
                    evaluateCalculatorsForPage(engine, currentPageId);
                }
                const rawUrl = action.clickActionData?.["url"] ?? '';
                const resolvedUrl = replaceCustomFieldTokens(rawUrl, sessionData?.sender);
                if (resolvedUrl.trim() !== '') {
                    if (action.clickActionData?.["openInNewTab"] === true) {
                        window.open(resolvedUrl, '_blank');
                    }
                    else {
                        window.open(resolvedUrl, '_self');
                    }
                }
                break;
            }
            case 'finishPresentation':
            {
                const finishActionResponse = resolveFinishPayloadText(action.clickActionData?.["finalOutcomeNotification"]).trim();
                const finishActionResponseStatus = resolveFinishPayloadText(action.clickActionData?.["finalOutcomeTitle"]).trim();
                let deferredOpenLink: { url: string; openInNewTab: boolean } | null = null;
                const pageId = engine?.scene?.getCurrentPage?.();
                if (engine && pageId) {
                    const tokenContext = sessionData?.sender
                        ? {
                            user: {
                                firstName: sessionData.sender.firstName,
                                lastName: sessionData.sender.lastName,
                                email: sessionData.sender.email
                            },
                            geolocation: sessionData.geolocation
                                ? {
                                    countryName: sessionData.geolocation.countryName,
                                    regionName: sessionData.geolocation.regionName,
                                    city: sessionData.geolocation.city
                                }
                                : undefined
                        }
                        : undefined;
                    const valid = await validateRequiredInputFields(engine, pageId, { replaceTokens: true, tokenContext });
                    if (!valid) {
                        break;
                    }
                }
                let finalActionSucceeded = true;

                // Handle finishPresentation actions that have a nested finalAction
                if (action.clickActionData?.["finalAction"])
                {
                    const finalAction = action.clickActionData?.["finalAction"];
                    switch(finalAction?.["type"]) {
                        case 'goToSlide':
                            if (!action.clickActionTarget) {
                                finalActionSucceeded = false;
                                break;
                            }
                            else if (action.clickActionTarget === 'nextSlide') {
                                finalActionSucceeded = await handleNext();
                            }
                            else if (action.clickActionTarget === 'previousSlide') {
                                finalActionSucceeded = await handlePrevious();
                            }
                            else {
                                const newSlideId = Number(action.clickActionTarget);
                                if (!Number.isNaN(newSlideId)) {
                                    finalActionSucceeded = await navigateToSlide(newSlideId);
                                } else {
                                    finalActionSucceeded = false;
                                }
                            }
                            break;
                        case 'openLink':
                            const rawFinalUrl = finalAction?.["url"] ?? '';
                            const resolvedFinalUrl = replaceCustomFieldTokens(rawFinalUrl, sessionData?.sender);
                            if (resolvedFinalUrl.trim() !== '') {
                                deferredOpenLink = {
                                    url: resolvedFinalUrl,
                                    openInNewTab: finalAction?.openInNewTab === true
                                };
                            }
                            break;
                        case 'noAction':
                            break;
                        default:
                    }
                }

                if (!finalActionSucceeded) {
                    break;
                }

                await markPresentationFinished({
                    actionResponse: finishActionResponse || undefined,
                    actionResponseStatus: finishActionResponseStatus || undefined
                });

                if (deferredOpenLink) {
                    if (deferredOpenLink.openInNewTab) {
                        window.open(deferredOpenLink.url, '_blank');
                    } else {
                        window.open(deferredOpenLink.url, '_self');
                    }
                }
                break;
            }
            case 'submit':
            {
                const submitVarName =
                    action?.clickActionData?.['submitVarName']
                    ?? '';
                const pageId = engine?.scene?.getCurrentPage?.();
                if (!engine || !pageId || !submitVarName) {
                    console.warn('Submit action missing required context or variable name.');
                    break;
                }
                const tokenContext = sessionData?.sender
                    ? {
                        user: {
                            firstName: sessionData.sender.firstName,
                            lastName: sessionData.sender.lastName,
                            email: sessionData.sender.email
                        },
                        geolocation: sessionData.geolocation
                            ? {
                                countryName: sessionData.geolocation.countryName,
                                regionName: sessionData.geolocation.regionName,
                                city: sessionData.geolocation.city
                            }
                            : undefined
                    }
                    : undefined;

                const { valid, values } = await collectInputFieldSubmission(
                    engine,
                    pageId,
                    submitVarName,
                    { replaceTokens: true, tokenContext }
                );
                if (!valid) {
                    break;
                }
                await submitSessionVariables(values);
                break;
            }
            case 'noAction':
                break;
        }
    }, [handleNext, handlePrevious, changeSlide, engine, presentationData, submitSessionVariables, markPresentationFinished]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            console.log('[DEBUG_LOG] Visibility changed:', document.visibilityState);
            if (document.hidden) {
                console.log('[DEBUG_LOG] Tab backgrounded, pausing playback.');
                setIsPlaying(false);
            } else {
                console.log('[DEBUG_LOG] Tab foregrounded, resuming playback.');
                setIsPlaying(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        handleClickActionRef.current = handleClickAction;
    }, [handleClickAction]);


    // Keyboard event listener for navigation and playback control
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check if an input element currently has focus
            const activeElement = document.activeElement;
            const isInputFocused = activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement ||
                activeElement instanceof HTMLSelectElement ||
                (activeElement as HTMLElement)?.isContentEditable;

            // Don't handle keyboard events if user is typing in an input
            if (isInputFocused) {
                return;
            }

            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    handleNext();
                    break;
                case ' ':
                    event.preventDefault();
                    if (isPlaying) {
                        handlePause();
                    } else {
                        handlePlay();
                    }
                    break;
            }
        };

        // Attach the event listener
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup function to remove the event listener when component unmounts
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isPlaying, handleNext, handlePrevious, handlePlay, handlePause]);

    const handleRecipientInfoSubmit = useCallback(async (info: RecipientInfo) => {
        // After information is validated, send to server (startPostALinkSession()), the server will return a
        // InvitationResponse that can be stored:
        if (!urlCode) {
            setRecipientModalError('Missing presentation link. Please refresh and try again.');
            return;
        }

        setRecipientModalError(null);
        setIsRecipientModalSubmitting(true);

        try {
            const postALinkResponse = await startPostALinkSession(
                urlCode,
                info.name,
                info.email,
                info.phone
            );
            if (sessionData) {
                setSessionData({
                    ...sessionData,
                    viewUuid: postALinkResponse.viewUuid,
                    geolocation: postALinkResponse.geolocation,
                    viewer: {
                        name: info.name,
                        email: info.email,
                        phone: info.phone
                    }
                });
            }

            // then the modal can be hidden to reveal the player.
            setIsRecipientModalOpen(false);

            // Now activate the engine so slide 1 is created and played with updated sessionData
            if (pendingEngineRef.current) {
                setEngine(pendingEngineRef.current);
                pendingEngineRef.current = null;
            }
        } catch (err) {
            console.error('[DEBUG_LOG] Failed to start Post-A-Link session', err);
            setRecipientModalError('Unable to start your session. Please try again.');
        } finally {
            setIsRecipientModalSubmitting(false);
        }
    }, [urlCode, sessionData]);

    // Effect to initialize the Creative Engine.
    useEffect(() => {

        if (config){
            let instance: CreativeEngine | null = null;
            const initEngine = async () => {
                try {
                    AudioContext.hookAudioContext();

                    // Run this BEFORE the custom element is defined/constructed.
                    (function patchAttachShadow() {
                        const orig = Element.prototype.attachShadow;
                        Element.prototype.attachShadow = function(init: ShadowRootInit) {
                            // Force open mode for specific tags if you want to limit scope
                            // if (this.tagName === 'CESDK-CANVAS') { ... }
                            const forced = { ...init, mode: 'open' as const };
                            const root = orig.call(this, forced);
                            (this as any).__shadowRoot = root; // stash for debugging if desired
                            return root;
                        };
                    })();

                    instance = await CreativeEngine.init(config);

                    // Configure default asset memory sizing
                    DeviceCompatibility.configureSizing(instance);

                    // Resource URI hook
                    instance.editor.setURIResolver(uriResolver);

                    //Set global scopes for what is allowed to be changed on the player
                    //Set editor/select to 'Allow' to enable click actions on blocks
                    //instance.editor.setGlobalScope('editor/select', 'Allow');
                    instance.editor.setGlobalScope('editor/select', 'Deny');
                    instance.editor.setGlobalScope('layer/move', 'Deny');
                    instance.editor.setGlobalScope('layer/resize', 'Deny');
                    instance.editor.setGlobalScope('layer/rotate', 'Deny');
                    instance.editor.setGlobalScope('layer/flip', 'Deny');
                    instance.editor.setGlobalScope('layer/crop', 'Deny');
                    instance.editor.setGlobalScope('layer/opacity', 'Deny');
                    instance.editor.setGlobalScope('layer/blendMode', 'Deny');
                    instance.editor.setGlobalScope('layer/visibility', 'Deny');
                    instance.editor.setGlobalScope('layer/clipping', 'Deny');

                    instance.editor.setSettingBool('controlGizmo/showMoveHandles', false);
                    instance.editor.setSettingBool('controlGizmo/showResizeHandles', false);
                    instance.editor.setSettingBool('controlGizmo/showRotateHandles', false);
                    instance.editor.setSettingBool('controlGizmo/showScaleHandles', false);
                    instance.editor.setSettingBool('controlGizmo/showCropHandles', false);
                    instance.editor.setSettingBool('page/title/show', false);

                    // attempt to start a view session, if not a PostALink, it will start immediately.
                    const invitationResponse = await startViewSession();

                    if (!invitationResponse) {
                        console.error('Invalid URL code');
                        throw new Error('Invalid URL code');
                    }
                    setSessionData(invitationResponse);
                    setPresentationData(invitationResponse.presentation);

                    if (!invitationResponse.presentation) {
                        console.error('Missing presentation data');
                        throw new Error('Missing presentation data');
                    }

                    // handle needs Post-A-Link data. Show modal, defer engine activation until modal is dismissed
                    if (invitationResponse.postALink) {
                        setIsRecipientModalOpen(true);
                    }

                    // get slides
                    const slideData = await fetchSlideData();
                    console.log('[DEBUG_LOG] slideData fetched:', slideData);

                    if (slideData)
                        DeviceCompatibility.adjustImageSizing(slideData);

                    if (slideData != null) {
                        const sortedSlides = reindexSlides(slideData);

                        // Initialize virtualized slides from fetched data
                        const initialVirtualSlides: VirtualizedSlideInfo[] = sortedSlides.map(s => ({
                            ...s,
                            loading: false,
                            loaded: false,
                            scenePageId: undefined
                        }));
                        setVirtualizedSlideInfo(initialVirtualSlides);
                        virtualizedSlideInfoRef.current = initialVirtualSlides;
                    }

                    // get block action data
                    const blockActionsData = invitationResponse.presentation.blockActions ? JSON.parse(invitationResponse.presentation.blockActions) : null;
                    console.log('blockActionsData', blockActionsData);

                    blockActionsRef.current = blockActionsData;
                    (window as any).__ClickVars = {};
                    setBlockActions(blockActionsData);

                    //Add the selected music, if it exists
                    const musicId: string | undefined = invitationResponse.presentation.musicId;
                    setSelectedMusicUrl(invitationResponse.presentation.musicUrl ?? null);

                    if (musicId) {
                        setSelectedSong(musicId);
                    } else {
                        setSelectedSong('none');
                    }

                    // IMPORTANT: Set engine LAST, after all data is loaded
                    // This ensures the engine setup useEffect has access to virtualizedSlideInfoRef.current
                    console.log('[DEBUG_LOG] About to setEngine, virtualizedSlideInfoRef.current:', virtualizedSlideInfoRef.current);

                    // For Post-A-Link, defer engine activation until the recipient modal is dismissed
                    // so slide 1 isn't created/played before sessionData is updated.
                    if (invitationResponse.postALink) {
                        pendingEngineRef.current = instance;
                    } else {
                        setEngine(instance);
                    }

                } catch (e: any) {
                    setError(new Error(`Engine initialization failed: ${e.message}`));
                    // setIsLoading(false);
                }
            };
            initEngine();

            return () => {
                clearPlaybackTimeout();
                instance?.dispose();
            };
        }
    }, [startViewSession, clearPlaybackTimeout, config]);

    function uriResolver(uri: string, defaultURIResolver: (URI: string) => string): string {
        let _uri = uri;

        // chain: mobile video changes
        _uri = DeviceCompatibility.uriResolver(_uri, defaultURIResolver);

        // chain: cors fix
        if (_uri.startsWith('https://alpha.myidecide.com/')) {
            return _uri.replace('https://alpha.myidecide.com/', `${window.location.origin}/`);
        }
        if (_uri.startsWith('http://localhost:8099/')) {
            return _uri.replace('http://localhost:8099/', `${window.location.origin}/`);
        }
        if (_uri.startsWith('https://my.idecide.com/')) {
            return _uri.replace('https://my.idecide.com/', `${window.location.origin}/`);
        }

        return _uri;
    }

    const handleResize = () => {
        // resize the engine scene
        if (engine && containerRef.current) {
            const scene = engine.scene.get();
            void engine.scene.zoomToBlock(
                scene!,
                { padding: 0 }
            );
        }

        // update any click action elements
        if (engine)
            repositionClickActionOverlayElements(engine);
    };

    // Reset to a fresh CE.SDK video scene exactly as originally created
    const resetToFreshVideoScene = async (engine: CreativeEngine): Promise<void> => {
        try {
            engine.scene.createVideo({
                page: {
                    size: {
                        width: PRESENTATION_WIDTH,
                        height: PRESENTATION_HEIGHT,
                    },
                }
            });

        } catch (e) {
            console.warn('Failed to create video scene via SDK', e);
        }
    };

    /**
     * Update visiblilty of the overlay plugins for a page.
     * @param pageId
     */
    const updateOverlayPlugins = (pageId: DesignBlockId) => {

        sliderControllerRef.current?.updateForActivePage(pageId);
        logoRevealControllerRef.current?.updateForActivePage(pageId);
        menuButtonControllerRef.current?.updateForActivePage(pageId);
        inputFieldControllerRef.current?.updateForActivePage(pageId);
    }



    const mountOverlayPlugins = (engine: CreativeEngine) => {
        const overlayContainer = document.getElementById('overlay-container');
        console.log('mountOverlayPlugins', overlayContainer);

        if (!engine || !overlayContainer) return;

        // Clear any existing overlay elements from previous page/navigation
        overlayContainer.innerHTML = '';

        // Dispose any previous overlays (route changes, remounts, etc.)
        sliderControllerRef.current?.cleanup();
        logoRevealControllerRef.current?.cleanup();
        menuButtonControllerRef?.current?.cleanup();
        inputFieldControllerRef.current?.cleanup();

        sliderControllerRef.current = mountSliderOverlays(engine, {
            container: overlayContainer,
            interactive: true,
            clearHTMLOverlays: true
        });

        const tokenContext = sessionData?.sender
            ? {
                user: {
                    firstName: sessionData.sender.firstName,
                    lastName: sessionData.sender.lastName,
                    email: sessionData.sender.email
                },
                viewer: {
                    name: sessionData.viewer?.name ?? "",
                    email: sessionData.viewer?.email ?? "",
                    phone: sessionData.viewer?.phone ?? ""
                },
                geolocation: sessionData.geolocation
                    ? {
                        countryName: sessionData.geolocation.countryName,
                        regionName: sessionData.geolocation.regionName,
                        city: sessionData.geolocation.city
                    }
                    : undefined
            }
            : undefined;

        inputFieldControllerRef.current = mountInputFieldOverlays(engine, {
            container: overlayContainer,
            interactive: true,
            replaceTokens: true,
            tokenContext,
            clearHTMLOverlays: true
        });

        remountLogoRevealOverlays();

        // Mount menu buttons and store controller
        menuButtonControllerRef.current = mountMenuButtonsForPlayer(engine, virtualizedSlideInfoRef, {
            container: overlayContainer,
            onNavigateToMenu: async (targetSlideId: number) => {
                await navigateToSlide(targetSlideId);
            },
            getCurrentSlideId: () => activeSlideIdRef.current,
            getCurrentPageId: () => engine.scene?.getCurrentPage?.(),
            getPageIdForSlide: (slideId: number | null | undefined) => {
                if (slideId == null) return null;
                return virtualizedSlideInfoRef?.current?.find((s: VirtualizedSlideInfo) => s.id=== slideId)?.scenePageId ?? null;
            },
            getSlideIdForPage: (pageId: number | null | undefined) => {
                if (pageId == null) return null;
                return getSlideIdForScenePage(pageId);
            }
        });
    }

    // Effect to append canvas and set up initial pages.
    useEffect(() => {
        if (engine && containerRef.current) {
            (async () => {

                    console.log('CreativeEngine',engine);
                    console.log('containerRef', containerRef.current);
                    await resetToFreshVideoScene(engine);

                    const scene = engine.scene.get();
                    console.log('the scene', scene);

                    // Remove default page
                    const defaultPages = engine.scene.getPages();
                    defaultPages?.forEach((page: DesignBlockId) => engine.block.destroy(page));

                    if (!virtualizedSlideInfoRef.current)
                        return;

                    const allSlides = virtualizedSlideInfoRef.current;

                    // Initialize virtual slides list with ALL slides (not loaded yet)
                    const initialVirtualSlides: VirtualizedSlideInfo[] = allSlides.map(s => ({
                        ...s,
                        loading: false,
                        loaded: false,
                        scenePageId: undefined
                    }));
                    virtualizedSlideInfoRef.current = initialVirtualSlides;
                    setVirtualizedSlideInfo(initialVirtualSlides);

                    await VideoRenderer.preloadVideosForMobile(engine, allSlides);

                    const slidesToPreload = allSlides.slice(0, SLIDES_MAX_PRECACHE);

                    await precacheInitialSlides(engine, slidesToPreload);

                    const loadedInitialSlides = virtualizedSlideInfoRef.current.filter(v => v.loaded && v.scenePageId !== undefined);
                    console.log('Initial slides loaded and kept:', loadedInitialSlides.map(v => v.slideIndex));

                    if (loadedInitialSlides?.length > 0) {
                        containerRef.current?.appendChild(engine.element as HTMLElement);

                        // Initial resize
                        handleResize();

                        // Listen for window resize
                        window.addEventListener('resize', handleResize);

                        // Use ResizeObserver for more reliable container size detection
                        const resizeObserver = new ResizeObserver(handleResize);
                        // @ts-ignore
                        resizeObserver.observe(containerRef.current);

                        //Apply JS style overrides for styles that cannot be changed through CSS, after a small delay
                        await new Promise(resolve => setTimeout(resolve, 375));
                        await injectStyles();
                        setOverlayPluginStyles();

                        //Setup overlay plugins for first slide
                        mountOverlayPlugins(engine);

                        const pageIds = engine.scene.getPages();
                        console.log('pageIds', pageIds);
                        setPages(pageIds);

                        if (pageIds.length > 0) {

                            const firstSlide = virtualizedSlideInfoRef?.current?.find((slide: VirtualizedSlideInfo) => slide.slideIndex == 0);
                            if (firstSlide != undefined) {
                                const initialSlideId = pageIds?.find((pageId: number, _index: number) => pageId == firstSlide.scenePageId);

                                if (!initialSlideId) {
                                    console.error(`Missing initial slide ${initialSlideId}`);
                                    return;
                                }

                                firstSlidePageRef.current = initialSlideId;

                                syncNamedTextBlockVariablesForScene(engine);
                                // Replace tokens in the intro slide before making it visible
                                await replaceTokensInPage(engine, initialSlideId);
                                evaluateCalculatorsForPage(engine, initialSlideId);

                                // Set initial slide visibility without resetting time yet
                                pageIds.forEach((pageId: DesignBlockId, _index: number) => {
                                    engine.block.setVisible(pageId, pageId === initialSlideId);

                                    //Make sure the elements aren't visible outside the canvas
                                    engine.block.setClipped(pageId, true);
                                });

                                // Reset playback position of the first slide
                                engine.block.setPlaybackTime(initialSlideId, 0);

                                // Set the active page state so overlays know which page is current
                                setActivePage(initialSlideId);
                                activePageRef.current = initialSlideId;
                                updateOverlayPlugins(initialSlideId);
                                if (firstSlide.id !== undefined) {
                                    setActiveSlideId(firstSlide.id);
                                    activeSlideIdRef.current = firstSlide.id;
                                }

                                engine.block.setPlaying(initialSlideId, true);
                                setIsPlaying(true); // Sync React state with engine state

                                if (!hasStartedFpsOverlayRef.current) {
                                    fpsOverlayRef.current = startFpsOverlay({ warnFps: 15, warnAfterMs: 1000 });
                                    hasStartedFpsOverlayRef.current = true;
                                }

                                requestAnimationFrame(() => {
                                    handleResize();
                                });
                            }
                        } else {
                            setError(new Error("No pages found in the presentation."));
                        }

                        // Cleanup function for resize listeners
                        return () => {
                            window.removeEventListener('resize', handleResize);
                            resizeObserver.disconnect();
                            sliderControllerRef.current?.cleanup();
                            sliderControllerRef.current = null;
                            logoRevealControllerRef.current?.cleanup();
                            logoRevealControllerRef.current = null;
                            menuButtonControllerRef.current?.cleanup();
                            menuButtonControllerRef.current = null;
                            fpsOverlayRef.current?.stop();
                            fpsOverlayRef.current = null;
                            hasStartedFpsOverlayRef.current = false;
                        };
                    }
                }
            )();
        }
    }, [engine]);


    useEffect(() => {
        // Cleanup sounds when activePage changes
        return () => {
            console.log('[DEBUG_LOG] activePage cleanup: disposing sounds for', activePage);
            soundDisposerRef.current?.();
            soundDisposerRef.current = null;
        };
    }, [activePage]);

    useEffect(() => {
        console.log('[DEBUG_LOG] Playback useEffect triggered:', { activePage, isPlaying, firstSlide: firstSlidePageRef?.current, playbackNonce });
        if (!engine) return;

        //Don't run for the first slide unless we specifically want to restart it
        // console.log('activePage useEffect',  activePage == firstSlidePageRef?.current);
        if (!activePage) {
            console.log('[DEBUG_LOG] useEffect early return: no activePage');
            return;
        }

        // If we are on the first slide and it's not playing, we usually skip this effect
        // BUT if it WAS just set to playing, we MUST allow it to run once to start the engine.
        // We can track if we've already started the first slide to avoid repeated starts if that's a concern.
        if (activePage == firstSlidePageRef?.current && !isPlaying) {
            console.log('[DEBUG_LOG] useEffect early return: first slide and not playing');
            return;
        }

        const currentPageId = activePage;
        // console.log('currentPageId', currentPageId);
        if (!currentPageId) return;

        // Check if current slide is a menu slide
        const currentVirtualizedSlide = virtualizedSlideInfoRef?.current.find((slide: VirtualizedSlideInfo) => slide.scenePageId == currentPageId);
        const isMenuSlide = currentVirtualizedSlide?.isMenuSlide === true;

        // Determine if background music should be playing for the current slide
        const isFirstSlide = virtualizedSlideInfoRef.current.find((s: VirtualizedSlideInfo) => s.id === activeSlideIdRef?.current)?.slideIndex === 0;
        const isMutedSlide = currentVirtualizedSlide?.muteBackgroundMusic === true;
        const shouldMusicBePlaying = isPlaying && !isFirstSlide && !isMutedSlide && !isMusicMutedRef.current;

        if (!isPlaying) {
            console.log('[DEBUG_LOG] isPlaying is FALSE, stopping engine playback for', currentPageId);
            // If paused, stop the media and clear any pending timer.
            engine.block.setPlaying(currentPageId, false);
            clearPlaybackTimeout();

            //Pause global playing music (unless we're paused for a logo reveal interaction, on a menu slide, or music is already off)
            if (!isPausedForLogoRevealRef.current && !isMenuSlide && !isMutedSlide && !isFirstSlide){
                if (MusicPlayer.isPlaying()) {
                    MusicPlayer.pause();
                }
            }

            // Pause manual sound player
            if (SoundPlayer.shouldUse()) {
                SoundPlayer.pause();
            }

            // Pause manual video renderer
            VideoRenderer.pause();

            // On menu slides, keep the music playing even when paused (unless user manually muted)
            if (isMenuSlide && !isMusicMutedRef.current) {
                MusicPlayer.unPause();
            }

            refreshMusicToggleButton();
            updateBackgroundMusicMix(currentPageId);
            return;
        }


        console.log('[DEBUG_LOG] isPlaying is TRUE, starting engine playback for', currentPageId, 'with nonce', playbackNonce);

        // Unpause sound player
        if (SoundPlayer.shouldUse()) {
            SoundPlayer.unPause();
        }

        // Unpause manual video renderer
        VideoRenderer.unPause();

        // Use setTimeout to give the engine time to fully prepare the page after resources are loaded
        // and the page is made visible. This ensures the engine's internal rendering pipeline is ready.
        // Extremely important.
        setTimeout(() => {
            if (activePageRef.current !== currentPageId) {
                return;
            }
            if (engine && engine.block.isValid(currentPageId)) {
                // Debug: List all page block IDs and their visibility status
                const allPages = engine.scene.getPages();
                console.log('[DEBUG] Slide about to start playing. All page blocks in engine:');
                allPages.forEach((pageId: DesignBlockId) => {
                    const isVisible = engine.block.isVisible(pageId);
                    console.log(`  Page ID: ${pageId}, Hidden: ${!isVisible}`);
                });

                // Hide loading overlay just before playing the first slide
                setLoadingOverlayVisible(false);
                if (isPausedForLogoRevealRef.current) {
                    return;
                }
                engine.block.setPlaying(currentPageId, true);
                const engineIsPlaying = engine.block.isPlaying(currentPageId);
                console.log('[DEBUG_LOG] After setPlaying(true) with setTimeout, engine.block.isPlaying returns:', engineIsPlaying);
            }
        }, 50);

        //Play or pause globally set music based on current slide state
        if (shouldMusicBePlaying) {
            MusicPlayer.unPause();
        }
        else if (!isMenuSlide && !isPausedForLogoRevealRef.current) {
            // Only pause if we're not on a menu slide and not paused for logo reveal
            MusicPlayer.pause();
        }
        refreshMusicToggleButton();
        updateBackgroundMusicMix(currentPageId);

        let totalDurationMs = (engine.block.getDuration(currentPageId) || (DEFAULT_SLIDE_DURATION_MS / 1000)) * 1000;
        console.log('totalDurationMs', totalDurationMs);
        const safeGetPlaybackTime = () => {
            try {
                if (activePageRef.current !== currentPageId) return null;
                if (!engine.block.isValid?.(currentPageId)) return null;
                if (engine.block.supportsPlaybackTime?.(currentPageId) === false) return null;
                return engine.block.getPlaybackTime(currentPageId);
            } catch {
                return null;
            }
        };

        // This correctly gets the current playback time without it being reset.
        const currentTimeSec = safeGetPlaybackTime() ?? 0;
        const currentTimeMs = currentTimeSec * 1000;
        remainingSlideTimeRef.current = Math.max(0, totalDurationMs - currentTimeMs);
        console.log('remainingSlideTimeMs', remainingSlideTimeRef.current);

        const pauseWhenFinishedLogoReveals = findAllLogoReveals(engine)
            .filter((logoBlockId) => {
                if (findOwningPage(engine, logoBlockId) !== currentPageId) {
                    return false;
                }
                const meta = getLogoRevealMeta(engine, logoBlockId);
                return meta.pauseWhileActive === true;
            })
            .map((logoBlockId) => {
                let durationSec = 0;
                try {
                    durationSec = engine.block.supportsDuration?.(logoBlockId)
                        ? (engine.block.getDuration(logoBlockId) || 0)
                        : 0;
                } catch {
                    durationSec = 0;
                }

                if (durationSec <= 0) return null;

                const startTimeSec = getAbsoluteStartOnPage(engine, logoBlockId, currentPageId);
                return {
                    blockId: logoBlockId,
                    endTimeSec: startTimeSec + durationSec
                };
            })
            .filter((logoReveal): logoReveal is { blockId: number; endTimeSec: number } => logoReveal !== null);

        const pauseForLogoRevealAtEnd = (playbackTimeSec: number) => {
            if (activePageRef.current !== currentPageId) {
                return;
            }
            if (!isPlayingRef.current || isPausedForLogoRevealRef.current) {
                return;
            }

            const pendingPauseReveal = pauseWhenFinishedLogoReveals.find(({ blockId, endTimeSec }) => {
                if (completedLogoRevealsRef.current.has(blockId)) return false;
                return playbackTimeSec >= Math.max(0, endTimeSec - LOGO_REVEAL_END_PAUSE_BUFFER_SEC);
            });

            if (!pendingPauseReveal) {
                return;
            }

            clearPlaybackTimeout();
            isPausedForLogoRevealRef.current = true;
            delayUntilVisibleRef.current = playbackTimeSec * 1000;

            logoRevealControllerRef.current?.updateForActivePage(currentPageId);
            try {
                engine.block.setPlaying(currentPageId, false);
            } catch {
            }
        };

        pauseForLogoRevealAtEnd(currentTimeSec);

        // Update wrapper's playback time attribute for overlay visibility tracking
        // Track last set value to only update when it changes
        let lastSetTime = -1;
        if (wrapperRef.current) {
            const roundedTime = Math.round((currentTimeMs / 1000) * 10) / 10; // Round to 1 decimal place
            wrapperRef.current.setAttribute('data-playback-time', String(roundedTime));
            lastSetTime = roundedTime;
        }

        // Start manual sound tracking, if needed
        if (SoundPlayer.shouldUse()) {
            if (!soundDisposerRef.current) {
                soundDisposerRef.current = SoundPlayer.startSoundPlayback(engine, currentPageId);
            }
        }

        // Async operations wrapped in IIFE
        (async () => {
            // Start manual video rendering, if needed
            const shouldForceRestart = playbackNonce > lastPlaybackNonceRef.current; // || currentPageId !== lastActivePageRef.current;
            await VideoRenderer.slideStarted(currentPageId, currentVirtualizedSlide!?.id!, shouldForceRestart);
            lastPlaybackNonceRef.current = playbackNonce;
            lastActivePageRef.current = currentPageId;
        })();

        // Set up interval to continuously update playback time attribute while playing
        const playbackTimeInterval = window.setInterval(() => {
            if (activePageRef.current !== currentPageId) {
                clearInterval(playbackTimeInterval);
                return;
            }
            if (wrapperRef.current && engine && currentPageId) {
                const currentTime = safeGetPlaybackTime();
                if (currentTime == null) {
                    clearInterval(playbackTimeInterval);
                    return;
                }
                const roundedTime = Math.round(currentTime * 10) / 10; // Round to 1 decimal place (10ths of a second)

                // Only update the attribute if the rounded value actually changed
                if (roundedTime !== lastSetTime) {
                    wrapperRef.current.setAttribute('data-playback-time', String(roundedTime));
                    lastSetTime = roundedTime;
                }

                pauseForLogoRevealAtEnd(currentTime);
            }
        }, 50); // Check every 50ms but only update when rounded time changes

        // Setup autoadvance timeout regardless of logo reveals,
        // but only if we are not already paused for one AND it's an auto-advance slide.
        if (!isPausedForLogoRevealRef.current && currentVirtualizedSlide?.autoAdvanceAfterNarration === true && !isMenuSlide) {
            console.log('[DEBUG_LOG] Scheduling auto-advance timeout', remainingSlideTimeRef.current);
            playbackTimeoutRef.current = window.setTimeout(async () => {
                // When time is up, advance to the next slide if the "autoadvanceafternarration" flag is set to true

                const hasPendingPauseReveal = pauseWhenFinishedLogoReveals.some(({ blockId }) =>
                    !completedLogoRevealsRef.current.has(blockId)
                );
                if (hasPendingPauseReveal) {
                    const playbackTimeAtTimeout = safeGetPlaybackTime() ?? (totalDurationMs / 1000);
                    pauseForLogoRevealAtEnd(playbackTimeAtTimeout);
                    return;
                }

                console.log('currentVirtualizedSlide timeout', currentVirtualizedSlide);
                if (currentVirtualizedSlide?.autoAdvanceAfterNarration == true) {
                    const slideToGo: string | undefined = currentVirtualizedSlide.autoAdvanceSlideId;
                    if (slideToGo && slideToGo !== '') {
                        const fromSlideId = activeSlideIdRef.current ?? currentVirtualizedSlide?.id;
                        if (fromSlideId != null) {
                            const targetSlide = resolveAutoAdvanceTarget(slideToGo, fromSlideId);
                            if (targetSlide?.id) {
                                const pageId = engine?.scene?.getCurrentPage?.();
                                if (engine && pageId) {
                                    const tokenContext = sessionData?.sender
                                        ? {
                                            user: {
                                                firstName: sessionData.sender.firstName,
                                                lastName: sessionData.sender.lastName,
                                                email: sessionData.sender.email
                                            },
                                            geolocation: sessionData.geolocation
                                                ? {
                                                    countryName: sessionData.geolocation.countryName,
                                                    regionName: sessionData.geolocation.regionName,
                                                    city: sessionData.geolocation.city
                                                }
                                                : undefined
                                        }
                                        : undefined;
                                    const valid = await validateRequiredInputFields(engine, pageId, { replaceTokens: true, tokenContext });
                                    if (!valid) {
                                        schedulePendingAutoAdvance(fromSlideId, targetSlide.id);
                                        return;
                                    }
                                }
                                await navigateToSlide(targetSlide.id);
                            }
                        }
                    }
                }
            }, remainingSlideTimeRef.current > 0 ? remainingSlideTimeRef.current : 0);
        } else {
            console.log('[DEBUG_LOG] Skipping auto-advance timeout. isPausedForLogoReveal:', isPausedForLogoRevealRef.current, 'autoAdvanceAfterNarration:', currentVirtualizedSlide?.autoAdvanceAfterNarration);
        }

        return () => {
            clearPlaybackTimeout();
            clearInterval(playbackTimeInterval);
            // DO NOT dispose sounds here, as this effect re-runs when isPlaying changes.
            // Disposal should only happen when the activePage changes.
        };

    }, [activePage, isPlaying, engine, navigateToSlide, resolveAutoAdvanceTarget, updateBackgroundMusicMix, schedulePendingAutoAdvance, presentationData, playbackNonce]);

    useEffect(() => {
        refreshMusicToggleButton();
        updateBackgroundMusicMix(activePage);
    }, [refreshMusicToggleButton, updateBackgroundMusicMix, activePage, virtualizedSlideInfo]);

    useEffect(() => {
        if (!engine) return;
        const handleResize = () => refreshMusicToggleButton();
        const unsubscribeZoom = engine.scene?.onZoomLevelChanged?.(handleResize);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (unsubscribeZoom) {
                try { unsubscribeZoom(); } catch {}
            }
        };
    }, [engine, refreshMusicToggleButton]);


    if (!config) {
         return <div className="loading-container"></div>;
     }

    if (error) {
        return <div className={'review-error'}><h1>Error Loading Presentation</h1><p>{error.message}</p></div>;
    }

    // Determine if we're on the first slide (slideIndex === 0)
    const currentSlide = virtualizedSlideInfoRef.current.find((slide: VirtualizedSlideInfo) => slide.id === activeSlideIdRef?.current);
    const isOnFirstSlide = currentSlide?.slideIndex === 0;

    return (
        <>
            <RecipientInfoModal
                open={isRecipientModalOpen}
                submitting={isRecipientModalSubmitting}
                error={recipientModalError}
                onSubmit={handleRecipientInfoSubmit}
            />
            <PlayPresentationButton
                handlePlay={handleStartPlayerEngine}
                isPlaying={isPlaying}
                isOnFirstSlide={isOnFirstSlide}
            />
            <div className="player-cesdk-wrapper" ref={wrapperRef} data-playback-time="0">
                <div id="cesdk_container" ref={containerRef}  className="review-cesdk-canvas" />
                <div id={'overlay-container'} className={isSlideTransitionPending ? 'overlay-container--transitioning' : undefined}></div>
                {isSlideTransitionPending && <div className="slide-transition-blocker" aria-hidden="true"></div>}
                <LoadingOverlay
                    visible={!isRecipientModalOpen && (isLoadingOverlayVisible || isSlideTransitionLoadingVisible)}
                    aspectRatio={loadingOverlayAspectRatio}
                    forceFill={isSlideTransitionLoadingVisible}
                />
            </div>
            {hasStarted && !isOnFirstSlide && (
                <Watermark
                    ref={watermarkRef}
                    senderName={sessionData?.sender ? `${sessionData.sender.firstName} ${sessionData.sender.lastName}`.trim() : undefined}
                    senderEmail={sessionData?.sender?.email}
                    engine={engine}
                    activePage={activePage}
                />
            )}
        </>
    );
}
